import { Suspense, lazy, useEffect, useMemo, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import {
  BAGLANTILAR, BORU_BAGLANTI, BOX_BAGLANTI, PIN_BAGLANTI, ROL_ETIKET, VARSAYILAN_BOY_MM,
  ekKaynagiMi, engelMetni, ipucuMetni, kapanisAlintilari, kaynakMalzemesi, kaynakNoOf, kesimKonumu, kesimKonumuEksikMi, kesimleriNormallestir, kesimOnerisi, kesimOzeti,
  kesimPlaniniDegerlendir, konumMetni, mesafeBeyanliMi, metreMetni, mmMetni, numaraOnerilmezMi, satirAdi, satirEtiketi, satirKonumu, uyariMetni,
  type Baglanti, type BoruReferansi, type BulguSonucu, type KaynakCumle, type KaynakCumleTuru, type KesimBaglami,
  type KesimGirdisiYaniti, type KesimOnerisi, type KesimOzeti, type KesimParcasi, type KesimPlaniGovdesi,
  type KesimPlaniTaslagi, type KesimSatiri, type Konum, type Rol,
} from "@oasis/domain";
import { Band, Buton, Cip, Etiket, Hata, alan, etiket as etiketSinifi, tabloBas, tabloHucre } from "@/components/parcalar";
import { pdfSayfasi, type SayfaAraligi } from "@/components/pdf-sayfa";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { tarihSaat } from "@/lib/veri";
/** Sayfa görüntüleyici yalnız bir "rw s.N" çipine tıklanınca yüklenir (triyajdaki gibi). */
const SurvrepGoruntuleyici = lazy(() => import("@/components/survrep").then((m) => ({ default: m.SurvrepGoruntuleyici })));

/**
 * D-035 / D-036: kesim planı onay penceresi ("Cut plan approval").
 *
 * İki yerde açılır: triyajda "Waiting Items" penceresinin 2. adımı (DogrulamaRiser.tsx) ve planı olmayan işin
 * komponent sayfasındaki "Approve cut plan" (Komponent.tsx). Öneri, değerlendirme (engel / uyarı / ipucu), kaynak
 * numarası ve ana malzeme @oasis/domain'den gelir; sunucu aynı fonksiyonlarla yeniden denetler. Burada yalnız
 * düzenleme durumu tutulur: boru referansı pencerede TEK (bütün işler aynı boru), kesim tablosu iş başına.
 * Şema prototipin kesim-sema sınıflarını kullanmaz: tema sınıfları + satır içi left:% / width:%.
 * D-043: plan gerekçesi (kesim iptali, birleştirme, "No cut") ve kesim notu (kusur bölgesindeki kesim dahil) isteğe
 * bağlıdır; onayı durdurmaz. Kusur bölgesindeki kesim yalnız uyarıdır.
 * U-037: kaynak cümlelerin "rw s.N" çipleri triyajdaki gibi Rework Scope PDF'ini o sayfada açar; PDF sistemde yoksa gri.
 * D-044: onaylı planın salt okunur görünümü (`OnayliKesimPlani`, komponent sayfasındaki "Show plan") aynı şemayı, kaynak
 * alıntılarını ve salt okunur kesim tablosunu (`OnayliKesimTablosu`, Cutting adım detayıyla ortak) kullanır.
 * D-054: boydan boya değişim önerisinde (karar "tam_boy") karar satırı yeşil, bulgu metnindeki kusur yerleri gri "Not cut points"
 * satırında sayılır; tablo başlığının ipucu ve şemanın kusur lejantı ("from finding text") buna göre değişir. İşin öteki boru
 * bulguları kesim önerildiğini söylemez, yerleri "Not cut points" satırına girer. "How this step works" listesine boydan boya
 * maddesi eklenir; özet tek parçayı "box flange weld – pin flange weld" diye yazar; kapanış alıntısı yokken onay cümlesi
 * "writes the recommended action" der (sunucu öncünün önerilen işini yazar).
 * D-055: yeni borunun ek kaynağı satırı (S): boydan boya öneride bir tane gelir, "+ Add splice weld" her plana ekler. Kesim değildir;
 * bağlantısı sabit Pipe to pipe butt, mesafesi isteğe bağlı (boşsa "position not set"; şemada sıralama konumunda kesikli turuncu
 * çizgi). Önerilen satır "✕ Cancel splice weld" ile iptal edilir, elle eklenen "✕ Remove" ile kalkar. Satır adları satirEtiketi'nden
 * (C kesim, S ek kaynağı); engel, uyarı ve ipucu metinleri aynı etiketle yazılır (sunucunun 400 metniyle aynı).
 * D-070: "No distance record available" kutusu (etkin Pipe to pipe butt kesim satırında ve ek kaynağı satırında, mesafe kutusunun altında).
 * İşaretlenince satırın mesafesi boşalır ve kutu kilitlenir ("no record"); işaret kaldırılınca hiçbir değer kendiliğinden geri gelmez.
 * Box / Pin bağlantısı seçilince işaret kalkar; iptal edilip "Restore" ile dönen satırda işaret geri gelir (ham satır tutulur, iptal
 * satır işaretsiz gönderilir: normalleştirme). Bir satır işaretliyken hiçbir satıra kaynak numarası önerilmez: "W" kutuları yalnız elle
 * yazılanı gösterir, yazılmamış kutu boş ve kırmızıdır (alan paketinin `kaynak_no_bos` engeli). Beyanlı satır şemaya çizilmez; şemanın
 * altında "Not on the schematic, no distance record: …" satırı sayar (öteki kalemlerin beyanlı kesimleri de). Beyanlı bir kesim varken
 * değişecek parça ve yeni boru boyu hesaplanmaz (özet bunu söyler). Onaylı planın salt okunur tablosu beyanlı satırda "no distance
 * record", mesafesi sonradan girilmiş satırda değerin altında "entered later · kişi · tarih saat" yazar; sonradan giriş komponent
 * sayfasındadır ("Welds of this item"), bu dosyada yoktur.
 */

/** Pencerede bir iş (bir kesim tablosu): anahtar triyajda "g:<öncü no>", komponent sayfasında iş kimliği. */
export interface KesimIsi { anahtar: string; yanit: KesimGirdisiYaniti }

/** İşin düzenleme durumu. `baglantiDegisen`: flanş satırında başka mesafe yazıldığı için Pipe to pipe'a dönen satırlar (sira). */
interface IsTaslagi { kesimYok: boolean; gerekce: string; kesimler: KesimSatiri[]; baglantiDegisen: number[] }

const kucukGirdi = "h-kontrol-kucuk rounded-md border border-line-strong bg-bg px-2 text-sm text-ink outline-none transition focus:border-orange disabled:opacity-60";
const bolumBasligi = "text-xs font-semibold uppercase tracking-wider text-ink-3";

/** "C2 and C3" · "C2, C3 and C4": son iki parça " and " ile bağlanır. */
export function veIle(p: string[]): string {
  if (p.length <= 1) return p.join("");
  return cevir`${p.slice(0, -1).join(", ")} and ${p[p.length - 1]}`;
}

/** Bağlantının flanş kaynağı yazısı; Pipe to pipe'ta null. */
export function flansYazisi(joint: string | null | undefined): string | null {
  if (joint === BOX_BAGLANTI) return cevir("at box flange weld");
  if (joint === PIN_BAGLANTI) return cevir("at pin flange weld");
  return null;
}
const flansMi = (b: string | null | undefined) => b === BOX_BAGLANTI || b === PIN_BAGLANTI;

/** Adet yazıları: tekil ve çoğul ayrı anahtar (Türkçede aynı, İngilizcede farklı). */
export const kesimAdedi = (n: number) => (n === 1 ? cevir("1 cut") : cevir`${n} cuts`);
const kaynakAdedi = (n: number) => (n === 1 ? cevir("1 weld") : cevir`${n} welds`);
const formAdedi = (n: number) => (n === 1 ? cevir("1 fit-up form") : cevir`${n} fit-up forms`);
/** D-055: "1 splice weld" · "2 splice welds" (Türkçede ikisi de "{0} ek kaynağı"). */
export const ekAdedi = (n: number) => (n === 1 ? cevir`${n} splice weld` : cevir`${n} splice welds`);

/**
 * Önerinin özeti (kesimOzeti sayılarından, çevrilebilir parçalarla). `pencere`: pencerenin özet satırı
 * ("proposal was 6 cuts: 2 cancelled (merged), 1 moved to box flange weld"); değilse iş sayfasındaki
 * "Cuts" satırı ("proposal 6; 2 cancelled, 1 moved to box flange weld", olay kaydıyla aynı söz).
 * D-055: elle eklenen ek kaynakları ayrı yazılır ("1 splice weld added"); öneride ek kaynağı varsa pencere "proposal was 2 cuts and
 * 1 splice weld: …" der.
 * D-070: "2 changed to Pipe to pipe butt" (flanş kesimi önerilip Pipe to pipe butt onaylanan satırlar; "moved to pin flange weld"
 * parçasından sonra) ve listenin sonunda "3 without distance record" (beyanlı satırlar). Önerisiz planda beyanlı satır varsa kısa
 * biçim yerine liste biçimi yazılır ("no proposal; 2 added, 2 without distance record"). Sıra alan paketinin `kesimOzeti.detay`ıyla aynı.
 */
export function oneriOzetMetni(o: KesimOzeti, pencere: boolean): string {
  const p: string[] = [];
  if (o.iptal) {
    p.push(!pencere || o.birlestirme === 0 ? cevir`${o.iptal} cancelled`
      : o.birlestirme === o.iptal ? cevir`${o.iptal} cancelled (merged)` : cevir`${o.iptal} cancelled (${o.birlestirme} merged)`);
  }
  if (o.boxa) p.push(cevir`${o.boxa} moved to box flange weld`);
  if (o.pina) p.push(cevir`${o.pina} moved to pin flange weld`);
  if (o.boruya) p.push(cevir`${o.boruya} changed to Pipe to pipe butt`);
  if (o.mesafeDegisen) p.push(cevir`${o.mesafeDegisen} distance changed`);
  if (o.eklenen) p.push(cevir`${o.eklenen} added`);
  if (o.ekEklenen) p.push(o.ekEklenen === 1 ? cevir`${o.ekEklenen} splice weld added` : cevir`${o.ekEklenen} splice welds added`);
  if (o.mesafesiz) p.push(cevir`${o.mesafesiz} without distance record`);
  if (o.oneri === 0) {
    if (!o.ekEklenen && !o.mesafesiz) return o.eklenen ? cevir`no proposal; ${o.eklenen} added` : cevir("no proposal");
    return cevir`no proposal; ${p.join(", ")}`;
  }
  if (!p.length) return cevir("as proposed");
  const liste = p.join(", ");
  if (!pencere) return cevir`proposal ${o.oneri}; ${liste}`;
  const oneriKesim = o.oneri - o.oneriEk;
  if (o.oneriEk) return cevir`proposal was ${kesimAdedi(oneriKesim)} and ${ekAdedi(o.oneriEk)}: ${liste}`;
  return o.oneri === 1 ? cevir`proposal was 1 cut: ${liste}` : cevir`proposal was ${o.oneri} cuts: ${liste}`;
}

/** Parça listesi "630–8,000 · 16,500–17,500". */
export const parcaMetni = (p: KesimParcasi[], ayrac = " · ") => p.map((x) => `${mmMetni(x.bas)}–${mmMetni(x.son)}`).join(ayrac);

/**
 * Sayı kutusu: yazarken değil, odaktan çıkınca (ya da Enter'da) kaydeder. Yarım yazılmış değer ("6" → "63" → "630")
 * flanş kilidini bozmasın, engeller her tuşta yanıp sönmesin. Boş bırakılırsa null.
 */
function SayiGirdisi({ deger, onKaydet, className, disabled, placeholder, hatali, etiket }: {
  deger: number | null; onKaydet: (n: number | null) => void; className?: string; disabled?: boolean; placeholder?: string; hatali?: boolean; etiket?: string;
}) {
  const [metin, setMetin] = useState<string | null>(null);
  const kaydet = () => {
    if (metin === null) return;
    const t = metin.trim();
    setMetin(null);
    if (t === "") { onKaydet(null); return; }
    const n = Number(t);
    if (Number.isFinite(n)) onKaydet(Math.round(n));
  };
  return (
    <input type="number" inputMode="numeric" aria-label={etiket} className={cn(kucukGirdi, hatali && "border-bad", className)} disabled={disabled} placeholder={placeholder}
      value={metin ?? (deger === null ? "" : String(deger))}
      onChange={(e) => setMetin(e.target.value)} onBlur={kaydet}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} />
  );
}

/* ---------- U-037: Rework Scope PDF'i ve "rw s.N" çipleri ---------- */

/**
 * Riserin Rework Scope PDF'i: triyajda riser künyesinden (reworkDokumanId, reworkRapor, reworkSayfaAraligi), komponent
 * sayfasında sunucunun `reworkBelgesi` alanından. `dokumanId` null ise çipler gri ve tıklanamaz.
 */
export interface ReworkBelgesi { dokumanId: string | null; ad: string | null; sayfaAraligi: SayfaAraligi | null }
/** Çipin tıklaması (kaynak cümlenin sayfası); PDF yoksa null. */
type RwAc = ((sayfa: number) => void) | null;

/**
 * "rw s.N" çipinin açtığı sayfa görüntüleyici (triyajdakinin aynısı). Görüntüleyici z-[80] ile pencerenin (z-[70]) üstünde
 * açılır; Esc ve dış tıklama yalnız onu kapatır. Sayfa D-034 aralığına göre kaydırılır (pdfSayfasi).
 * U-039: komponent sayfasındaki bulgu kartı aynı kancayı SURVREP belgesi için de kullanır (mavi "s.N").
 */
export function useReworkPdf(rework: ReworkBelgesi | null | undefined): { ac: RwAc; goruntuleyici: ReactNode } {
  const [sayfa, setSayfa] = useState<number | null>(null);
  const dokumanId = rework?.dokumanId ?? null;
  const aralik = rework?.sayfaAraligi ?? null;
  const ac: RwAc = dokumanId ? (s) => setSayfa(pdfSayfasi(s, aralik)) : null;
  const goruntuleyici = dokumanId && sayfa !== null ? (
    <Suspense fallback={<div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 text-sm text-white">{cevir("Loading viewer...")}</div>}>
      <SurvrepGoruntuleyici dokumanId={dokumanId} ad={rework?.ad ?? null} sayfa={sayfa} onKapat={() => setSayfa(null)} />
    </Suspense>
  ) : null;
  return { ac, goruntuleyici };
}

/** Kaynak cümlesinin sayfa çipi (rw s.N): triyajdaki gibi mor düğme; PDF sistemde yoksa gri, tıklanamaz. */
function SayfaCipi({ sayfa, ac }: { sayfa: number; ac: RwAc }) {
  return ac
    ? <button type="button" className="ml-1 whitespace-nowrap rounded bg-hold-bg px-1.5 py-0.5 text-xs font-medium text-hold hover:opacity-80" onClick={() => ac(sayfa)}>rw s.{sayfa}</button>
    : <span className="ml-1 whitespace-nowrap rounded bg-surface-2 px-1.5 py-0.5 text-xs text-ink-3" title={cevir("Rework Scope PDF is not in system")}>rw s.{sayfa}</span>;
}

/** Birebir alıntı listesi; aynı sayfadan ardışık alıntılarda çip yalnız sonuncuda. */
function Alintilar({ liste, ac }: { liste: KaynakCumle[]; ac: RwAc }) {
  return (
    <div className="mt-1 space-y-0.5 border-l-2 border-line-strong pl-2 text-sm text-ink-2">
      {liste.map((k, i) => {
        const sonraki = liste[i + 1];
        const cip = k.sayfa !== null && (!sonraki || sonraki.sayfa !== k.sayfa);
        return <div key={i}><i>“{k.metin}”</i>{cip && k.sayfa !== null && <SayfaCipi sayfa={k.sayfa} ac={ac} />}</div>;
      })}
    </div>
  );
}

/**
 * Cutting adım detayındaki satır içi kaynak alıntıları (her alıntı kendi çipiyle); "rw s.N" çipi Rework Scope PDF'ini açar.
 * Görüntüleyici bu bileşenle birlikte çizilir (adım detayı penceresinin üstünde).
 */
export function SatirIciAlintilar({ liste, rework }: { liste: KaynakCumle[]; rework?: ReworkBelgesi | null }) {
  const { ac, goruntuleyici } = useReworkPdf(rework);
  return <>{liste.map((k, i) => <span key={i}>“{k.metin}”{k.sayfa !== null && <SayfaCipi sayfa={k.sayfa} ac={ac} />} </span>)}{goruntuleyici}</>;
}

const KAYNAK_SIRASI: KaynakCumleTuru[] = ["kapanis", "harita", "bulgu", "onerilen", "ek", "dispo"];
const kaynakBasligi = (t: KaynakCumleTuru) => ({
  kapanis: cevir("Closing disposition, verbatim"),
  harita: cevir("Corrosion map, Below Tolerance entries, verbatim"),
  bulgu: cevir("Finding text, verbatim"),
  onerilen: cevir("Recommended action, verbatim"),
  ek: cevir("Rework ADDITIONAL row, verbatim"),
  dispo: cevir("Job instruction, verbatim"),
})[t];

/** Bir bulgunun kaynak cümleleri, türe göre başlıklı (onay penceresi ve salt okunur plan aynı sırayı çizer). */
function TureGoreAlintilar({ liste, ac }: { liste: KaynakCumle[]; ac: RwAc }) {
  return (
    <>
      {KAYNAK_SIRASI.map((t) => {
        const l = liste.filter((k) => k.tur === t);
        return l.length ? (
          <div key={t}>
            <div className="mt-2 text-xs font-medium text-ink-3">{kaynakBasligi(t)}</div>
            <Alintilar liste={l} ac={ac} />
          </div>
        ) : null;
      })}
    </>
  );
}

/** Bulgunun kesim yeri (flanş/girth kuralı): Girth Weld 1 = box, son = pin, aradakiler ara kaynaklar. */
function bulguYeri(b: BulguSonucu, r: BoruReferansi): { tur: "box" | "pin" } | { tur: "ara"; mm: number } | null {
  if (b.girthNo !== null) {
    if (b.girthNo <= 1) return { tur: "box" };
    if (b.girthNo >= 2 + r.ara.length) return { tur: "pin" };
    const mm = r.ara[b.girthNo - 2];
    return mm !== undefined ? { tur: "ara", mm } : { tur: "pin" };
  }
  return b.taraf ? { tur: b.taraf } : null;
}

/**
 * Bulgunun karar satırı (öneri nereden okundu, neden güvenilir ya da değil). `tamBoyIs` (D-054): işin önerisi boydan boya;
 * işin öteki boru bulgularının aralık kesimleri düşmüştür, bu bulgular kesim önerildiğini söylemez (yerleri kesimDisiSatiri'nda).
 * `ekSatiri` (D-055): işin ilk boydan boya bulgusu; yeşil satırın altında yeni borunun ek kaynağı önerisi bir kez yazılır.
 */
function kararSatiri(b: BulguSonucu, r: BoruReferansi, tamBoyIs: boolean, ekSatiri: boolean): ReactNode {
  const k = b.konumlar.map(konumMetni).join(" · ");
  const yer = bulguYeri(b, r);
  if (tamBoyIs && b.tur === "boru" && b.karar !== "tam_boy") return null;
  switch (b.karar) {
    case "uyumlu":
      return <div className="mt-2 text-xs text-ok">{cevir`Read from the closing disposition: ${k} mm. The corrosion map agrees, so cuts are proposed.`}</div>;
    case "harita_yok":
      return <div className="mt-2 text-xs text-warn">{cevir`Read from the closing disposition: ${k} mm. No corrosion map entry to cross-check; confirm the cuts.`}</div>;
    case "celiski":
      return (
        <Band tip="not" className="mt-2">
          <b>{cevir("Disposition and corrosion map disagree; add cut points.")}</b>{" "}
          {cevir`Closing disposition: ${k || "—"} mm · corrosion map Below Tolerance: ${b.haritaKonumlari.map(konumMetni).join(" · ") || "—"} mm.`}
        </Band>
      );
    case "metinden":
      return <div className="mt-2 text-xs text-warn">{cevir`Read from the finding text: ${k} mm — from finding text, confirm.`}</div>;
    case "tam_boy":
      // D-054: iki flanş kaynağında kesim; bulgu metnindeki kusur yerleri kesim noktası değil, şemada kalır.
      return (
        <>
          <div className="mt-2 text-xs text-ok">
            {b.tamBoyKaynagi === "kapanis"
              ? cevir("Full-length replacement: the closing disposition says to replace the full length of pipe. Two cuts are proposed, one at each flange weld — confirm.")
              : cevir("Full-length replacement: the recommended action says to cut both connectors off and replace the full length of pipe. Two cuts are proposed, one at each flange weld — confirm.")}
          </div>
          {ekSatiri && <div className="text-xs text-ok">{cevir("New pipe: a full-length pipe is joined from at least two lengths, so one splice weld on the new pipe is proposed (no cut). Add one for each further length.")}</div>}
          {k && <div className="text-xs text-ink-3">{cevir`Not cut points: the defect locations in the finding text (${k} mm); they stay on the schematic.`}</div>}
        </>
      );
    case "kalem_adindan":
      if (b.girthNo !== null && yer) {
        return (
          <div className="mt-2 text-xs text-warn">
            {yer.tur === "ara" ? cevir`Girth Weld ${b.girthNo} is the intermediate girth weld at ${mmMetni(yer.mm)} mm (Girth Weld 1 = box, last = pin) — confirm.`
              : yer.tur === "box" ? cevir`Girth Weld ${b.girthNo} is the box flange weld (Girth Weld 1 = box, last = pin) — confirm.`
                : cevir`Girth Weld ${b.girthNo} is the pin flange weld (Girth Weld 1 = box, last = pin) — confirm.`}
          </div>
        );
      }
      return <div className="mt-2 text-xs text-warn">{yer?.tur === "box" ? cevir("Cut at the box flange weld, from the item name — confirm.") : cevir("Cut at the pin flange weld, from the item name — confirm.")}</div>;
    case "cozum_yok":
      return (
        <div className="mt-2 text-xs text-warn">
          {yer?.tur === "box" ? cevir("No solution text in the disposition; cut at the box flange weld from the item name — confirm.")
            : yer?.tur === "ara" ? cevir`No solution text in the disposition; cut at the intermediate girth weld at ${mmMetni(yer.mm)} mm from the item name — confirm.`
              : cevir("No solution text in the disposition; cut at the pin flange weld from the item name — confirm.")}
        </div>
      );
    case "kaynak_tamiri":
      return <div className="mt-2 text-xs text-ink-2">{yer?.tur === "box" ? cevir("Connector weld repair: one weld repair at the existing box flange weld, no cut.") : cevir("Connector weld repair: one weld repair at the existing pin flange weld, no cut.")}</div>;
    default:
      return <div className="mt-2 text-xs text-warn">{cevir("No cut point could be read from the texts; add cut points or choose No cut.")}</div>;
  }
}

/**
 * Kesim noktası sayılmayan metinler: Above Tolerance girdileri, "splice weld at …", et kalınlığı değerleri. D-054: boydan boya
 * işte öteki boru bulgusunun okunan yerleri de (aralık kesimleri düştü; şemada kalırlar).
 */
function kesimDisiSatiri(b: BulguSonucu, kaynaklar: KaynakCumle[], tamBoyIs: boolean): ReactNode {
  if (b.tur !== "boru") return null;
  const p: string[] = [];
  if (tamBoyIs && b.karar !== "tam_boy" && b.konumlar.length) p.push(`${b.konumlar.map(konumMetni).join(" · ")} mm`);
  if (b.ustTolerans.length) p.push(cevir`Above Tolerance entries (${b.ustTolerans.map(metreMetni).join(", ")})`);
  for (const y of b.yoksayilanKaynak) p.push(`“${y}”`);
  if (kaynaklar.some((k) => /\d\s*mm\b|\bWT\b/i.test(k.metin))) p.push(cevir("wall thickness values"));
  return p.length ? <div className="text-xs text-ink-3">{cevir`Not cut points: ${veIle(p)}.`}</div> : null;
}

/** Kaynak bloğu: işin bütün bulgularının cümleleri (grup = tek iş, bütün bulgular), karar satırı ve dışarıda kalanlar. */
function KaynakBlogu({ yanit, oneri, ac }: { yanit: KesimGirdisiYaniti; oneri: KesimOnerisi; ac: RwAc }) {
  const is = yanit.is;
  const lider = is.kalemler.find((k) => k.no === is.lider) ?? is.kalemler[0];
  const bulgular: (BulguSonucu | null)[] = oneri.bulgular.length ? oneri.bulgular : [null];
  // D-055: öneri işe bir ek kaynağı satırı koyar; satırı ilk boydan boya bulgunun altında bir kez söyle.
  const ilkTamBoy = oneri.bulgular.find((b) => b.tur === "boru" && b.karar === "tam_boy") ?? null;
  return (
    <div className="mb-3 rounded-md border border-line p-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <b className="text-sm">{isBasligi(yanit)}</b>
        {is.st && <Etiket>{[is.st, is.stAd].filter(Boolean).join(" ")}</Etiket>}
        {lider?.siparisPn && <span className="text-xs text-ink-3">{cevir("order PN")} <b className="font-mono">{lider.siparisPn}</b></span>}
      </div>
      {bulgular.map((b, i) => {
        const no = b ? b.no : null;
        const kaynaklar = oneri.kaynaklar.filter((k) => k.no === no);
        const kalem = no !== null ? is.kalemler.find((k) => k.no === no) : undefined;
        return (
          <div key={i} className={cn(i > 0 && "mt-3 border-t border-dashed border-line pt-2")}>
            {bulgular.length > 1 && kalem && <div className="text-sm font-medium">#{kalem.no} {kalem.kalem}</div>}
            <TureGoreAlintilar liste={kaynaklar} ac={ac} />
            {b && kararSatiri(b, oneri.referans, oneri.tamBoy, b === ilkTamBoy)}
            {b?.celiskili && <div className="text-xs text-warn">{cevir("The source record is marked CONFLICTING (human approval).")}</div>}
            {b && kesimDisiSatiri(b, kaynaklar, oneri.tamBoy)}
          </div>
        );
      })}
    </div>
  );
}

/** İşin başlığı: "#1 Body overall + coating" · grupta "#61 Pin end … + #62 Pin end threads". */
export function isBasligi(y: KesimGirdisiYaniti): string {
  const k = y.is.kalemler;
  if (!k.length) return [y.is.komponentAd, y.is.st].filter(Boolean).join(" · ");
  return k.map((x) => `#${x.no} ${x.kalem}`).join(" + ");
}
/** Şema ve uyarılar için kısa ad: "#1" · "#61 + #62". */
const isKisaAdi = (y: KesimGirdisiYaniti) => (y.is.nolar.length ? y.is.nolar.map((n) => `#${n}`).join(" + ") : y.is.st ?? "");

/**
 * Aynı komponentin başka işlerinin onaylı kesimleri (yalnız uyarı; aynı mesafe kuralı iş içinde, Soru 2). D-055: öteki işin ek
 * kaynağı satırı kesim değildir, listeye girmez (sunucunun TamirService.kesimBaglami'yle aynı süzgeç).
 */
function digerKesimleri(y: KesimGirdisiYaniti): { etiket: string; mesafeMm: number }[] {
  return y.digerKesimler.flatMap((d) => d.kesimler.flatMap((k) => (k.durum === "onayli" && k.mesafeMm !== null && !ekKaynagiMi(k) ? [{ etiket: d.nolar.map((n) => `#${n}`).join(" + "), mesafeMm: k.mesafeMm }] : [])));
}
/**
 * D-070: öteki kalemin onaylı planındaki beyanlı (mesafe kaydı olmayan, hâlâ boş) kesim sayısı. Bu kesimler 500 mm yakınlık bağlamına
 * girmez (mesafeleri yok) ve şemaya çizilmez; sayı uyarı ("nearness to them cannot be checked") ve şemanın altındaki satır içindir.
 * Ek kaynağı kesim değildir (sunucunun TamirService.kesimBaglami'yla aynı süzgeç).
 */
const digerMesafesizAdedi = (d: KesimGirdisiYaniti["digerKesimler"][number]): number =>
  d.kesimler.filter((k) => k.durum === "onayli" && k.mesafeMm === null && k.mesafeKayitYok === true && !ekKaynagiMi(k)).length;
/** D-070: uyarı bağlamı: öteki kalem başına beyanlı kesim sayısı; 0 olan kalem listede yok. */
function digerMesafesizleri(y: KesimGirdisiYaniti): { etiket: string; adet: number }[] {
  return y.digerKesimler.map((d) => ({ etiket: d.nolar.map((n) => `#${n}`).join(" + "), adet: digerMesafesizAdedi(d) })).filter((d) => d.adet > 0);
}

/* ---------------- şema ---------------- */

/** `ek` (D-055): yeni borunun ek kaynağı (kesim değil); `konumsuz`: mesafesi girilmemiş, sıralama konumunda çizilir. */
export interface SemaKesimi { etiket: string; mm: number; iptal: boolean; ek?: boolean; konumsuz?: boolean }
export interface SemaIsareti { etiket: string; konumlar: number[] }

/**
 * Taslağın şemadaki kesimleri (flanş satırı referans boşsa uca çizilir); `onek` çok işli pencerede "#8 ". D-055: ek kaynağı "S3"
 * etiketiyle; mesafesizse ilk değişecek parçanın ortasında (kaynak numarası sıralamasıyla aynı konum).
 * D-070: beyanlı satırın (kesim ya da ek kaynağı) konumu yoktur (`satirKonumu` boş döner), çizilmez; `semaDisiEtiketleri` sayar.
 */
export function semaKesimleri(t: KesimPlaniTaslagi, onek = ""): SemaKesimi[] {
  return t.kesimler.flatMap((s) => {
    const k = satirKonumu(t, s);
    const ek = ekKaynagiMi(s);
    return k === null ? [] : [{ etiket: `${onek}${satirAdi(s)}`, mm: k, iptal: s.durum === "iptal", ek, konumsuz: ek && s.mesafeMm === null }];
  });
}

/** D-070: şemaya çizilmeyen beyanlı satırların etiketleri, sıra numarasıyla ("C1", "S3"; çok işli pencerede "#8 C1"). */
export function semaDisiEtiketleri(t: KesimPlaniTaslagi, onek = ""): string[] {
  return t.kesimler.filter(mesafeBeyanliMi).map((s) => `${onek}${satirAdi(s)}`);
}
/** D-070: şemanın altındaki satır "Not on the schematic, no distance record: C1 · C2 · S3"; beyanlı satır yoksa boş liste. */
export function semaDisiSatirlari(etiketler: string[]): string[] {
  return etiketler.length ? [cevir`Not on the schematic, no distance record: ${etiketler.join(" · ")}`] : [];
}

const etiketSinif = "absolute whitespace-nowrap text-[11px] leading-[14px]";

/**
 * Boru şeması (ölçeksiz): box ucundan mm ÷ boy. Var olan kaynaklar gri, kusur bantları kırmızı, değişecek parçalar
 * taralı, kesimler C1…Cn (iptal kesikli ve üstü çizili), başka işlerin onaylı kesimleri gri, bu aktarımda olmayan
 * bekleyen kalemler kesikli kutu. Dar ekranda yatay kayar. `bulgudanKusur` (D-054, boydan boya değişim önerisi): kusur
 * bantları bulgu metnindeki yerlerdir, lejant "defect zone (from finding text)" der. D-055: ek kaynağı kesikli turuncu çizgi
 * (iptalde iptal kesim gibi kesikli gri), etiketi kesim etiketleri sırasında; mesafesizse altında soluk "position not set" yazısı.
 * D-070: `altSatirlar` eksenin altında, lejantın üstünde yazı satırlarıdır: şemaya çizilmeyen beyanlı satırlar ("Not on the schematic,
 * no distance record: C1 · C2") ve öteki kalemlerin beyanlı kesimleri. Yeni lejant girdisi yok.
 */
export function KesimSemasi({ referans, bantlar, kesimler, parcalar, digerler = [], bekleyenler = [], bulgudanKusur = false, altSatirlar = [] }: {
  referans: BoruReferansi; bantlar: Konum[]; kesimler: SemaKesimi[]; parcalar: KesimParcasi[];
  digerler?: SemaIsareti[]; bekleyenler?: SemaIsareti[]; bulgudanKusur?: boolean; altSatirlar?: string[];
}) {
  const boy = referans.boy && referans.boy > 0 ? referans.boy : VARSAYILAN_BOY_MM;
  const oran = (mm: number) => Math.min(100, Math.max(0, (mm / boy) * 100));
  const sol = (mm: number) => `${oran(mm)}%`;
  /** Uçlara yakın yazı içeri doğru açılır (box'ta sağa, pin'de sola), ortadaki ortalanır. */
  const hiza = (mm: number) => (oran(mm) < 15 ? undefined : oran(mm) > 85 ? "translateX(-100%)" : "translateX(-50%)");
  const kaynaklar: { mm: number; ad: string; donus: string | undefined }[] = [];
  const pinNo = 2 + referans.ara.length;
  if (referans.box !== null) kaynaklar.push({ mm: referans.box, ad: cevir`Girth Weld ${1} · Box to pipe (existing)`, donus: undefined });
  referans.ara.forEach((mm, i) => kaynaklar.push({ mm, ad: cevir`Girth Weld ${i + 2} · Pipe to pipe (splice)`, donus: "translateX(-50%)" }));
  if (referans.pin !== null) kaynaklar.push({ mm: referans.pin, ad: cevir`Girth Weld ${pinNo} · Pin to pipe`, donus: "translateX(-100%)" });
  const iptalVar = kesimler.some((k) => k.iptal);
  const ekVar = kesimler.some((k) => k.ek);
  const digerVar = digerler.length > 0 || bekleyenler.length > 0;
  const tarali = "repeating-linear-gradient(135deg, var(--brand-orange) 0 2px, transparent 2px 6px)";
  return (
    <div>
      <div className="scroll-thin overflow-x-auto">
        <div className="min-w-[820px]">
          <div className="flex items-start gap-2">
            <div className="w-[34px] flex-none pt-6 text-[11px] font-semibold leading-[14px] text-ink-2">BOX</div>
            <div className="relative h-[116px] flex-auto">
              <div className="absolute inset-x-0 top-[22px] h-[18px] rounded-[3px] border border-line-strong bg-surface-2" />
              {bantlar.map((b, i) => (
                // Nokta kusur (bas = son) konumun üstünde ortalanmış ince bant olarak çizilir.
                <div key={`k${i}`} className="absolute top-[23px] h-4 bg-bad"
                  style={b.son > b.bas ? { left: sol(b.bas), width: `${oran(b.son) - oran(b.bas)}%`, minWidth: 4, opacity: 0.45 } : { left: sol(b.bas), width: 4, marginLeft: -2, opacity: 0.45 }} />
              ))}
              {parcalar.map((p, i) => (
                <div key={`p${i}`} className="absolute top-[23px] h-4" style={{ left: sol(p.bas), width: `${Math.max(0, oran(p.son) - oran(p.bas))}%`, backgroundImage: tarali }} />
              ))}
              {kaynaklar.map((k, i) => <div key={`w${i}`} className="absolute top-4 h-[30px] w-0 border-l-[3px] border-ink-3" style={{ left: sol(k.mm), marginLeft: -1 }} />)}
              {digerler.flatMap((d, i) => d.konumlar.map((mm, j) => <div key={`d${i}-${j}`} className="absolute top-[15px] h-8 w-0 border-l-2 border-ink-3" style={{ left: sol(mm), marginLeft: -1 }} />))}
              {bekleyenler.flatMap((d, i) => d.konumlar.map((mm, j) => <div key={`b${i}-${j}`} className="absolute top-[17px] h-7 w-4 rounded border-2 border-dashed border-ink-3" style={{ left: sol(mm), marginLeft: -8 }} />))}
              {kesimler.map((k, i) => (
                <div key={`c${i}`} className={cn("absolute top-[15px] h-8 w-0 border-l-2", k.iptal ? "border-dashed border-ink-3" : k.ek ? "border-dashed border-orange" : "border-ink")}
                  style={{ left: sol(k.mm), marginLeft: -1 }} />
              ))}
              {kesimler.map((k, i) => (
                <span key={`ce${i}`} className={cn(etiketSinif, "top-0", k.iptal ? "font-normal text-ink-3" : "font-semibold text-ink")} style={{ left: sol(k.mm), transform: "translateX(-50%)" }}>
                  {k.iptal ? <s>{k.etiket}</s> : k.etiket}
                </span>
              ))}
              {kesimler.filter((k) => k.ek && k.konumsuz && !k.iptal).map((k, i) => (
                <span key={`ek${i}`} className={cn(etiketSinif, "top-[50px] text-ink-3 opacity-70")} style={{ left: sol(k.mm), transform: hiza(k.mm) }}>
                  {cevir("new pipe splice weld · position not set")}
                </span>
              ))}
              {kaynaklar.map((k, i) => (
                <span key={`we${i}`}>
                  <span className={cn(etiketSinif, "top-[50px] text-ink-2")} style={{ left: sol(k.mm), transform: k.donus }}>{k.ad}</span>
                  <span className={cn(etiketSinif, "top-[66px] text-ink-3")} style={{ left: sol(k.mm), transform: k.donus }}>{mmMetni(k.mm)}</span>
                </span>
              ))}
              {bantlar.map((b, i) => (
                <span key={`ke${i}`} className={cn(etiketSinif, "top-[82px] text-bad")} style={{ left: sol((b.bas + b.son) / 2), transform: "translateX(-50%)" }}>{konumMetni(b)}</span>
              ))}
              {[...digerler, ...bekleyenler].map((d, i) => {
                const mm = d.konumlar[0];
                return mm === undefined ? null : <span key={`de${i}`} className={cn(etiketSinif, "top-[98px] text-ink-3")} style={{ left: sol(mm), transform: hiza(mm) }}>{d.etiket}</span>;
              })}
            </div>
            <div className="w-[34px] flex-none pt-6 text-right text-[11px] font-semibold leading-[14px] text-ink-2">PIN</div>
          </div>
          <div className="mx-[42px] flex justify-between text-[11px] leading-[14px] text-ink-3"><span>0</span><span>{mmMetni(boy)} mm</span></div>
        </div>
      </div>
      {altSatirlar.map((m, i) => <div key={`as${i}`} className="mt-2 text-xs text-ink-2">{m}</div>)}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-2">
        <span><span className="mr-1 inline-block h-2.5 w-[3px] bg-ink-3 align-[-1px]" />{cevir("existing weld")}</span>
        <span><span className="mr-1 inline-block h-2.5 w-3.5 rounded-sm bg-bad align-[-1px]" style={{ opacity: 0.45 }} />{bulgudanKusur ? cevir("defect zone (from finding text)") : cevir("defect zone (Below Tolerance)")}</span>
        <span><span className="mr-1 inline-block h-2.5 w-3.5 rounded-sm bg-surface-2 align-[-1px]" style={{ backgroundImage: tarali }} />{cevir("piece to be replaced")}</span>
        <span><span className="mr-1 inline-block h-3 w-0.5 bg-ink align-[-1px]" />{cevir("cut point")}</span>
        {ekVar && <span><span className="mr-1 inline-block h-3 w-0 border-l-2 border-dashed border-orange align-[-1px]" />{cevir("new pipe splice weld (no cut)")}</span>}
        {iptalVar && <span><span className="mr-1 inline-block h-3 w-0 border-l-2 border-dashed border-ink-3 align-[-1px]" />{cevir("cancelled cut")}</span>}
        {digerVar && <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm border-2 border-dashed border-ink-3 align-[-1px]" />{cevir("other item, not in this transfer")}</span>}
      </div>
    </div>
  );
}

/* ---------------- geniş pencere ---------------- */

/**
 * Kesim planı penceresinin kabı: parcalar.tsx Modal'ının geniş hâli (şema 820 px, tablo 900 px). Dış tıklama
 * pencereyi kapatmaz: uzun bir düzenleme yanlışlıkla kaybolmasın; Esc, ✕ ve Cancel kapatır.
 */
export function GenisModal({ baslik, onKapat, children, kilitli }: { baslik: ReactNode; onKapat: () => void; children: ReactNode; kilitli?: boolean }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && !kilitli && onKapat();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onKapat, kilitli]);
  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-4">
      <div className="max-h-[92dvh] w-full overflow-y-auto rounded-t-2xl bg-surface p-4 shadow-[var(--shadow-lg)] sm:max-w-5xl sm:rounded-xl sm:p-5">
        <div className="mb-3 flex items-start justify-between gap-3">
          <h2 className="text-md font-semibold">{baslik}</h2>
          <button onClick={onKapat} disabled={kilitli} className="rounded-md p-1 text-ink-3 hover:bg-surface-2 disabled:opacity-40" aria-label={cevir("Close")}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ---------------- pencerenin kendisi ---------------- */

/** Pencerenin ilk boru referansı: ilk işin önerisi, ara kaynaklar bütün işlerden birleşik. */
function ilkReferans(oneriler: KesimOnerisi[]): BoruReferansi {
  const ilk = oneriler[0]?.referans ?? { box: null, ara: [], pin: null, boy: VARSAYILAN_BOY_MM };
  const ara = [...new Set(oneriler.flatMap((o) => o.referans.ara))].sort((a, b) => a - b);
  return { ...ilk, ara };
}

/** D-070: satırdan "No distance record available" işaretini çıkarır (anahtar satırda kalmaz, tanımsız bırakılmaz). */
function beyansiz(s: KesimSatiri): KesimSatiri {
  const { mesafeKayitYok: _beyan, ...kalan } = s;
  return kalan;
}

/**
 * D-070: "No distance record available" kutusunun satıra etkisi (saf). İşaretlenince satır işaretlenir ve mesafesi boşalır;
 * kaldırılınca yalnız işaret kalkar, mesafe boş kalır (kesimde kırmızı kutu, ek kaynağında "optional"): hiçbir değer kendiliğinden
 * geri gelmez. İptal edilen satır ham hâliyle tutulur; işaret normalleştirmede (alan paketi) yalnız etkin satırda kalır, bu yüzden
 * "Restore" ile dönen satırda geri gelir.
 */
export function beyanIsaretle(s: KesimSatiri, isaretli: boolean): KesimSatiri {
  return isaretli ? { ...s, mesafeMm: null, mesafeKayitYok: true } : beyansiz(s);
}

/**
 * Bağlantı seçiminin satıra etkisi (saf): Box/Pin mesafeyi o flanş kaynağına kilitler (referans boşsa mesafe boş); Pipe to pipe mesafeyi
 * olduğu gibi bırakır. Flanştan Pipe to pipe'a dönen satır o anki flanş kaynağı mesafesinden başlar (ham satırdaki değer eski
 * referanstan kalmış olabilir). D-070: Box / Pin seçilince "No distance record available" işareti kalkar (flanş satırının mesafesi boru
 * referansıdır, beyan taşımaz).
 */
export function baglantiUygula(s: KesimSatiri, b: Baglanti, r: Pick<BoruReferansi, "box" | "pin">): KesimSatiri {
  const flansMm = (x: Baglanti) => (x === BOX_BAGLANTI ? r.box : x === PIN_BAGLANTI ? r.pin : null);
  return {
    ...(flansMi(b) ? beyansiz(s) : s), baglanti: b,
    mesafeMm: flansMi(b) ? flansMm(b) : flansMi(s.baglanti) ? flansMm(s.baglanti) ?? s.mesafeMm : s.mesafeMm,
  };
}

/**
 * Kesim tablosunda etkin satırın mesafe hücresi ("Distance from box end (mm)"). `s` normalleştirilmiş satır. Kaynak tamiri satırında
 * kutu yok, yalnız yazı. Öteki satırlarda sayı kutusu (solunda, değer öneriden farklıysa "proposed" ve üstü çizili önerilen değer),
 * altında yazılar: ek kaynağında "optional · …", Box / Pin kesiminde "at … flange weld · locked", flanş satırına başka mesafe yazıldıysa
 * (`baglantiDegisti`) "joint changed to Pipe to pipe butt".
 * D-070: Pipe to pipe butt kesim satırında ve ek kaynağı satırında kutunun altında "No distance record available" kutusu (Box / Pin
 * satırında ve kaynak tamirinde yok). İşaretliyken sayı kutusu kilitli ve gridir ("no record"), kırmızı kenar yoktur, altında
 * "approved without a distance; it can be entered later on the job page" yazar ve ek kaynağının "optional · …" yazısı çıkmaz.
 * İşaretsiz ve boş kesimde kutu bugünkü gibi kırmızıdır.
 */
export function MesafeHucresi({ s, baglantiDegisti, onMesafe, onBeyan }: {
  s: KesimSatiri; baglantiDegisti: boolean; onMesafe: (n: number | null) => void; onBeyan: (isaretli: boolean) => void;
}) {
  const flans = flansYazisi(s.baglanti);
  const ek = ekKaynagiMi(s);
  if (s.tur === "kaynak_tamiri") {
    return <span>{s.mesafeMm !== null ? (flans ? `${mmMetni(s.mesafeMm)} · ${flans}` : mmMetni(s.mesafeMm)) : flans ?? "—"}</span>;
  }
  const onerilen = s.oneriMm !== null && s.oneriMm !== s.mesafeMm;
  const beyanSunulur = ek || (s.tur === "kesim" && s.baglanti === BORU_BAGLANTI);
  const beyanli = mesafeBeyanliMi(s);
  return (
    <>
      <div className="flex items-center gap-2">
        {onerilen && <span className="whitespace-nowrap text-xs text-ink-3">{cevir("proposed")} <s>{mmMetni(s.oneriMm ?? 0)}</s></span>}
        <SayiGirdisi className="w-24" etiket={cevir("Distance from box end (mm)")} deger={s.mesafeMm} hatali={s.mesafeMm === null && !flans && !ek && !beyanli}
          disabled={beyanli} placeholder={beyanli ? cevir("no record") : ek ? cevir("optional") : undefined} onKaydet={onMesafe} />
      </div>
      {ek && !beyanli && <div className="mt-1 text-xs text-ink-3">{cevir("optional · enter when the pipe lengths are known")}</div>}
      {!ek && flans && <div className="mt-1 text-xs text-ink-3">{s.baglanti === BOX_BAGLANTI ? cevir("at box flange weld · locked") : cevir("at pin flange weld · locked")}</div>}
      {!ek && !flans && baglantiDegisti && <div className="mt-1 text-xs text-warn">{cevir("joint changed to Pipe to pipe butt")}</div>}
      {beyanSunulur && (
        <label className="mt-1 flex items-center gap-2 whitespace-nowrap text-xs">
          <input type="checkbox" checked={beyanli} onChange={(e) => onBeyan(e.target.checked)} /> {cevir("No distance record available")}
        </label>
      )}
      {beyanli && <div className="mt-1 text-xs text-ink-3">{cevir("approved without a distance; it can be entered later on the job page")}</div>}
    </>
  );
}

/**
 * Kesim tablosunda etkin satırın "Resulting weld" hücresi: "W" + numara kutusu, bağlantı ve yer, altında ana malzeme. `no` kutuda
 * görünen numara (`kaynakNoOf`): elle yazılan, yoksa önerilen. `hatali`: numara engeli bu satırı anıyor (kırmızı kenar).
 * D-070: planda beyanlı satır varken numara önerilmez, `no` yalnız elle yazılandır; yazılmamış kutu boş ve kırmızıdır
 * (`kaynak_no_bos`). Beyanlı satırın yeri "no distance record" ("—" işaretsiz boş kesimde, "position not set" işaretsiz boş ek
 * kaynağında kalır).
 */
export function KaynakHucresi({ s, no, hatali, malzeme, onNo }: { s: KesimSatiri; no: number | null; hatali: boolean; malzeme: string; onNo: (n: number | null) => void }) {
  const yer = s.mesafeMm !== null ? `${mmMetni(s.mesafeMm)} mm`
    : mesafeBeyanliMi(s) ? cevir("no distance record") : ekKaynagiMi(s) ? cevir("position not set") : flansYazisi(s.baglanti) ?? "—";
  return (
    <>
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1"><b>W</b>
          <SayiGirdisi className="w-16" etiket={cevir("Weld number")} deger={no} hatali={hatali} onKaydet={onNo} />
        </span>
        <span className="whitespace-nowrap">{s.baglanti} · {yer}</span>
      </div>
      <div className="mt-1 text-xs text-ink-3">{malzeme}</div>
    </>
  );
}

/**
 * Engel yokken onay düğmesinin üstündeki yeşil cümle (saf). D-043: gerekçe onayın şartı değil; cümle gerekçeden söz etmez. D-055:
 * etkin ek kaynağı varsa cümlenin sonunda "and 1 splice weld on the new pipe" (ek kaynağı kesim sayısına girmez). D-070: pencerede
 * beyanlı satır varken (`mesafesiz` > 0) cümle mesafe aralığından söz etmez ("all between 0 and …" her kesim için doğru değildir):
 * "Approve is enabled: 2 cuts, 2 welds without a distance record."; öteki beş biçim değişmez.
 */
export function onayAcikMetni(p: { kesimsiz: boolean; kesim: number; ek: number; mesafesiz: number; boy: number }): string {
  if (p.kesimsiz) return cevir("Approve is enabled: No cut; the item goes down with 0 welds.");
  if (p.mesafesiz > 0) {
    return p.ek > 0
      ? cevir`Approve is enabled: ${kesimAdedi(p.kesim)} and ${ekAdedi(p.ek)} on the new pipe, ${kaynakAdedi(p.mesafesiz)} without a distance record.`
      : cevir`Approve is enabled: ${kesimAdedi(p.kesim)}, ${kaynakAdedi(p.mesafesiz)} without a distance record.`;
  }
  if (p.ek > 0) {
    return p.kesim === 1
      ? cevir`Approve is enabled: 1 active cut, between 0 and ${mmMetni(p.boy)} mm, and ${ekAdedi(p.ek)} on the new pipe.`
      : cevir`Approve is enabled: ${p.kesim} active cuts, all between 0 and ${mmMetni(p.boy)} mm, no duplicate distances, and ${ekAdedi(p.ek)} on the new pipe.`;
  }
  return p.kesim === 1
    ? cevir`Approve is enabled: 1 active cut, between 0 and ${mmMetni(p.boy)} mm.`
    : cevir`Approve is enabled: ${p.kesim} active cuts, all between 0 and ${mmMetni(p.boy)} mm, no duplicate distances.`;
}

/**
 * Kesim planı onayı (pencerenin gövdesi). `ust` triyajda adım çipleri ve giriş cümleleri. `onOnayla` iş başına
 * normalleştirilmiş planı alır (öneri alanları dahil; sunucu yeniden hesaplar). "Cancel" hiçbir şey göndermez,
 * taslak tutulmaz; pencere yeniden açılınca öneri baştan gelir. `rework`: riserin Rework Scope PDF'i (U-037, "rw s.N").
 */
export function KesimPlaniAdimi({ isler, ust, onGeri, onIptal, onOnayla, onayMetni, mesgul, hata, rework }: {
  isler: KesimIsi[];
  ust?: ReactNode;
  onGeri?: () => void;
  onIptal: () => void;
  onOnayla: (planlar: Record<string, KesimPlaniGovdesi>) => Promise<void>;
  onayMetni: string;
  mesgul?: boolean;
  hata?: string | null;
  rework?: ReworkBelgesi | null;
}) {
  const { ac, goruntuleyici } = useReworkPdf(rework);
  const oneriler = useMemo(() => isler.map((i) => kesimOnerisi(i.yanit.girdi)), [isler]);
  const [referans, setReferans] = useState<BoruReferansi>(() => ilkReferans(oneriler));
  const [bosAra, setBosAra] = useState(false);
  const [taslaklar, setTaslaklar] = useState<Record<string, IsTaslagi>>(() =>
    Object.fromEntries(isler.map((i, n) => [i.anahtar, { kesimYok: false, gerekce: "", kesimler: oneriler[n]?.kesimler ?? [], baglantiDegisen: [] }])));
  const coklu = isler.length > 1;
  const triyaj = isler.every((i) => i.yanit.is.tamirId === null);

  const hesap = isler.map((i, n) => {
    const oneri = oneriler[n] as KesimOnerisi;
    const d = taslaklar[i.anahtar] ?? { kesimYok: false, gerekce: "", kesimler: [], baglantiDegisen: [] };
    const t = kesimleriNormallestir({ referans, kesimYok: d.kesimYok, gerekce: d.gerekce, kesimler: d.kesimler });
    // D-070: öteki kalemlerin beyanlı kesimleri (digerMesafesiz) yakınlık bağlamına girmez; "cannot be checked" uyarısını besler.
    const baglam: KesimBaglami = {
      bantlar: oneri.kusurBantlari, hidrolik: oneri.hidrolikUyarisi, rotaKod: i.yanit.is.rotaKod, digerKesimler: digerKesimleri(i.yanit), digerMesafesiz: digerMesafesizleri(i.yanit),
    };
    // D-055: satır etiketi (C kesim, S ek kaynağı) engel, uyarı ve ipucu metinlerinde sunucunun 400 metniyle aynı.
    // D-070: `numaraYazilir` = planda beyanlı satır var (kaynak numarası önerilmez); `konumEksik` = beyanlı satırlardan biri kesim (parçalar hesaplanmaz).
    return {
      is: i, oneri, d, t, deg: kesimPlaniniDegerlendir(t, baglam), ozet: kesimOzeti(t), onek: coklu ? `${isKisaAdi(i.yanit)} ` : "", etiket: satirEtiketi(t),
      numaraYazilir: numaraOnerilmezMi(t), konumEksik: kesimKonumuEksikMi(t),
    };
  });
  const engelVar = hesap.some((h) => h.deg.engeller.length > 0);
  const boy = referans.boy ?? VARSAYILAN_BOY_MM;

  /* düzenleme */
  const guncelle = (a: string, f: (d: IsTaslagi) => IsTaslagi) => setTaslaklar((p) => {
    const d = p[a];
    return d ? { ...p, [a]: f(d) } : p;
  });
  const satir = (a: string, sira: number, f: (s: KesimSatiri) => KesimSatiri) =>
    guncelle(a, (d) => ({ ...d, kesimler: d.kesimler.map((s) => (s.sira === sira ? f(s) : s)) }));
  const degisenCikar = (d: IsTaslagi, sira: number) => d.baglantiDegisen.filter((x) => x !== sira);
  const flansMm = (b: Baglanti) => (b === BOX_BAGLANTI ? referans.box : b === PIN_BAGLANTI ? referans.pin : null);
  /** Bağlantı seçimi (kural `baglantiUygula`'da): Box/Pin mesafeyi o flanş kaynağına kilitler ve D-070 işaretini kaldırır. */
  const baglantiSec = (a: string, sira: number, b: Baglanti) => guncelle(a, (d) => ({
    ...d, baglantiDegisen: degisenCikar(d, sira),
    kesimler: d.kesimler.map((s) => (s.sira !== sira ? s : baglantiUygula(s, b, referans))),
  }));
  /** D-070: "No distance record available" kutusu (kural `beyanIsaretle`'de). Yalnız pencere durumu; Approve'dan önce hiçbir şey gönderilmez. */
  const beyanDegistir = (a: string, sira: number, isaretli: boolean) => satir(a, sira, (s) => beyanIsaretle(s, isaretli));
  /** Flanş satırında başka mesafe yazılırsa bağlantı görünür biçimde Pipe to pipe'a döner. */
  const mesafeYaz = (a: string, s: KesimSatiri, n: number | null) => {
    if (s.tur === "kesim" && flansMi(s.baglanti)) {
      if (n === null || n === flansMm(s.baglanti)) return;
      guncelle(a, (d) => ({ ...d, baglantiDegisen: [...degisenCikar(d, s.sira), s.sira], kesimler: d.kesimler.map((x) => (x.sira === s.sira ? { ...x, baglanti: BORU_BAGLANTI, mesafeMm: n } : x)) }));
      return;
    }
    satir(a, s.sira, (x) => ({ ...x, mesafeMm: n }));
  };
  const iptalEt = (a: string, siralar: number[], neden: "iptal" | "birlestirme") =>
    guncelle(a, (d) => ({ ...d, kesimler: d.kesimler.map((s) => (siralar.includes(s.sira) ? { ...s, durum: "iptal", iptalNedeni: neden } : s)) }));
  const geriAl = (a: string, sira: number) => satir(a, sira, (s) => ({ ...s, durum: "onayli", iptalNedeni: null }));
  /** "+ Add cut point" (tur kesim) ve D-055 "+ Add splice weld" (tur ek_kaynagi): Pipe to pipe butt, mesafesiz, öneri alanları boş. */
  const ekle = (a: string, tur: "kesim" | "ek_kaynagi" = "kesim") => guncelle(a, (d) => ({
    ...d,
    kesimler: [...d.kesimler, {
      sira: d.kesimler.reduce((m, s) => Math.max(m, s.sira), 0) + 1, tur, mesafeMm: null, baglanti: BORU_BAGLANTI,
      durum: "onayli", iptalNedeni: null, not: null, kaynakNo: null, oneriMm: null, oneriBaglanti: null,
    }],
  }));
  /** Elle eklenen satır (öneride yok) silinebilir; arkasındaki eklenenler bir sıra kayar, C1…Cn boşluksuz kalır. */
  const kaldir = (a: string, sira: number) => guncelle(a, (d) => ({
    ...d,
    baglantiDegisen: d.baglantiDegisen.filter((x) => x !== sira).map((x) => (x > sira ? x - 1 : x)),
    kesimler: d.kesimler.filter((s) => s.sira !== sira).map((s) => (s.sira > sira ? { ...s, sira: s.sira - 1 } : s)),
  }));
  /** "No cut": etkin satırlar "kesim_yok" nedeniyle iptal olur; geri alınınca yalnız onlar döner. */
  const kesimYokDegistir = (a: string) => guncelle(a, (d) => d.kesimYok
    ? { ...d, kesimYok: false, kesimler: d.kesimler.map((s) => (s.durum === "iptal" && s.iptalNedeni === "kesim_yok" ? { ...s, durum: "onayli", iptalNedeni: null } : s)) }
    : { ...d, kesimYok: true, kesimler: d.kesimler.map((s) => (s.durum === "onayli" ? { ...s, durum: "iptal", iptalNedeni: "kesim_yok" } : s)) });

  const onayla = () => onOnayla(Object.fromEntries(hesap.map((h) => [h.is.anahtar, {
    referans: h.t.referans, kesimYok: h.t.kesimYok, gerekce: h.t.gerekce, kesimler: h.t.kesimler,
  } satisfies KesimPlaniGovdesi])));

  /* boru referansı */
  const ilkOneri = oneriler[0];
  const kanit = (a: "box" | "ara" | "pin" | "boy") => {
    const k = ilkOneri?.referansKaniti.find((x) => x.alan === a);
    if (!k) return null;
    if (k.durum === "metinden") return <><div className="mt-1 text-xs text-ok">{cevir("from text")}</div>{k.metin && <div className="text-xs text-ink-3">“{k.metin}”</div>}</>;
    if (k.durum === "varsayilan") return <div className="mt-1 text-xs text-info">{cevir("default 22,860 mm (75 ft); change if the MRB weld map gives another length")}</div>;
    if (k.durum === "baska_plan") {
      const is = (k.metin ?? isler[0]?.yanit.digerKesimler[0]?.tamirId ?? "").slice(0, 8);
      return <div className="mt-1 text-xs text-info">{cevir`from the approved cut plan of job ${is}`}</div>;
    }
    return <div className="mt-1 text-xs text-warn">{cevir("not stated in the reports")}</div>;
  };
  const araYaz = (i: number | null, n: number | null) => {
    setReferans((r) => {
      const ara = [...r.ara];
      if (i === null) { if (n !== null) ara.push(n); }
      else if (n === null) ara.splice(i, 1);
      else ara[i] = n;
      return { ...r, ara: [...new Set(ara)].sort((x, y) => x - y) };
    });
    if (i === null && n !== null) setBosAra(false);
  };

  /* şema */
  const semaKesim = hesap.flatMap((h) => semaKesimleri(h.t, h.onek));
  const semaBant = hesap.flatMap((h) => h.oneri.kusurBantlari);
  // D-054: lejant "from finding text" yalnız bütün bantlar boydan boya bulguların bulgu metninden geliyorsa (korozyon haritası yok).
  const tamBoyVar = hesap.some((h) => h.oneri.tamBoy);
  const bulgudanKusur = tamBoyVar && hesap.every((h) => h.oneri.bulgular.every((b) =>
    b.tur !== "boru" || (b.haritaKonumlari.length === 0 && (b.karar === "tam_boy" || b.konumlar.length === 0))));
  const semaParca = hesap.flatMap((h) => h.deg.parcalar);
  // D-070: şemaya çizilmeyen beyanlı satırlar ("Not on the schematic, no distance record: C1 · C2 · S3"); ardından öteki kalemler.
  const semaAlt: string[] = semaDisiSatirlari(hesap.flatMap((h) => semaDisiEtiketleri(h.t, h.onek)));
  const gorulenIs = new Set<string>();
  const semaDiger: SemaIsareti[] = [];
  for (const h of hesap) {
    for (const d of h.is.yanit.digerKesimler) {
      if (gorulenIs.has(d.tamirId)) continue;
      gorulenIs.add(d.tamirId);
      const digerAdi = cevir`${d.nolar.map((n) => `#${n}`).join(" + ")} (approved)`;
      // D-055: öteki işin ek kaynağı satırı onaylı kesim işareti olarak çizilmez.
      const konumlar = d.kesimler.flatMap((k) => (k.durum === "onayli" && !ekKaynagiMi(k) ? [kesimKonumu(k, referans)] : [])).filter((x): x is number => x !== null);
      if (konumlar.length) semaDiger.push({ etiket: digerAdi, konumlar });
      // D-070: öteki kalemin beyanlı kesimleri konumsuzdur, işaret almaz; şemanın altında sayılır.
      const mesafesiz = digerMesafesizAdedi(d);
      if (mesafesiz === 1) semaAlt.push(cevir`${digerAdi}: 1 cut without a distance record, not on the schematic`);
      else if (mesafesiz > 1) semaAlt.push(cevir`${digerAdi}: ${mesafesiz} cuts without a distance record, not on the schematic`);
    }
  }
  const pencereNolari = new Set(hesap.flatMap((h) => h.is.yanit.is.nolar));
  const gorulenNo = new Set<number>();
  const semaBekleyen: SemaIsareti[] = [];
  for (const h of hesap) {
    for (const b of h.is.yanit.bekleyenler) {
      if (pencereNolari.has(b.no) || gorulenNo.has(b.no)) continue;
      gorulenNo.add(b.no);
      const konumlar = b.kesimler.map((k) => kesimKonumu(k, referans)).filter((x): x is number => x !== null);
      if (konumlar.length) semaBekleyen.push({ etiket: cevir`${`#${b.no} ${b.kalem}${b.st ? ` (${b.st})` : ""}`}: waiting, not in this transfer`, konumlar });
    }
  }

  /* onay durumu */
  const toplamKesim = hesap.reduce((n, h) => n + h.deg.etkinKesim, 0);
  const toplamKaynak = hesap.reduce((n, h) => n + h.deg.kaynakSayisi, 0);
  const toplamEk = hesap.reduce((n, h) => n + h.deg.ekKaynagi, 0);
  /** D-070: penceredeki beyanlı satırlar (mesafe kaydı olmayan kaynaklar: kesim ve ek kaynağı). */
  const toplamMesafesiz = hesap.reduce((n, h) => n + h.deg.mesafesiz, 0);
  const iptalVar = hesap.some((h) => h.t.kesimler.some((s) => s.durum === "iptal"));
  const hepsiKesimsiz = hesap.every((h) => h.t.kesimYok);
  // D-054 (8): bütün işler boydan boya ve kapanış alıntısı yok → sunucu öncünün önerilen işini talimat yazar (muayene.controller.ts).
  const onerilenTalimat = hesap.length > 0 && hesap.every((h) =>
    h.oneri.tamBoy && kapanisAlintilari(h.is.yanit.girdi) === null && Boolean(h.is.yanit.girdi.bulgular[0]?.onerilenIs));
  const onayDurumu = engelVar ? (
    <div className="text-xs text-bad">
      <b>{cevir("Approve is disabled:")}</b>
      <ul className="mt-0.5 list-disc space-y-0.5 pl-5">
        {hesap.flatMap((h) => h.deg.engeller.map((e, i) => <li key={`${h.is.anahtar}-${i}`}>{coklu ? `${isKisaAdi(h.is.yanit)}: ` : ""}{cevir(engelMetni(e, h.etiket))}</li>))}
      </ul>
    </div>
  ) : (
    // Yeşil cümlenin altı biçimi `onayAcikMetni`'nde (D-043, D-055, D-070).
    <div className="text-xs text-ok">{onayAcikMetni({ kesimsiz: hepsiKesimsiz, kesim: toplamKesim, ek: toplamEk, mesafesiz: toplamMesafesiz, boy })}</div>
  );

  return (
    <div>
      {ust}
      {hesap.map((h) => <KaynakBlogu key={h.is.anahtar} yanit={h.is.yanit} oneri={h.oneri} ac={ac} />)}

      {/* D-036: boru referansı, box ucundan mm; pencerede tek (bütün işler aynı boru) */}
      <div className="mb-3 rounded-md border border-line p-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2"><b className="text-sm">{cevir("Tube reference")}</b><span className="text-xs text-ink-3">{cevir("mm from box end · editable")}</span></div>
        {/* D-036: kanıt MRB weld map sayfasıdır (statik sayfada R002 s.33 çipi). Sayfa sistemde yapılandırılmış olarak yok; çip nereye bakılacağını söyler. */}
        <div className="mt-1"><Cip>{cevir("Evidence: the MRB weld map of this component (weld sequence and O.A.L.). Enter the values from it.")}</Cip></div>
        <div className="mt-2 grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="min-w-0">
            <label className={etiketSinifi}>{cevir("Box flange weld")}</label>
            <SayiGirdisi className="h-kontrol w-full px-2.5" etiket={cevir("Box flange weld")} deger={referans.box} onKaydet={(n) => setReferans((r) => ({ ...r, box: n }))} />
            {kanit("box")}
          </div>
          <div className="min-w-0">
            <label className={etiketSinifi}>{cevir("Intermediate girth weld")}</label>
            <div className="space-y-1">
              {referans.ara.map((mm, i) => (
                <div key={`${i}-${mm}`} className="flex items-center gap-1">
                  <SayiGirdisi className="h-kontrol w-full px-2.5" etiket={cevir("Intermediate girth weld")} deger={mm} onKaydet={(n) => araYaz(i, n)} />
                  <button type="button" className="rounded-md px-1.5 text-sm text-ink-3 hover:bg-surface-2" aria-label={cevir("Remove")} onClick={() => araYaz(i, null)}>✕</button>
                </div>
              ))}
              {(referans.ara.length === 0 || bosAra) && <SayiGirdisi className="h-kontrol w-full px-2.5" etiket={cevir("Intermediate girth weld")} deger={null} onKaydet={(n) => araYaz(null, n)} />}
              {referans.ara.length > 0 && !bosAra && <Buton kucuk onClick={() => setBosAra(true)}>{cevir("+ Add")}</Buton>}
            </div>
            {kanit("ara")}
          </div>
          <div className="min-w-0">
            <label className={etiketSinifi}>{cevir("Pin flange weld")}</label>
            <SayiGirdisi className="h-kontrol w-full px-2.5" etiket={cevir("Pin flange weld")} deger={referans.pin} onKaydet={(n) => setReferans((r) => ({ ...r, pin: n }))} />
            {kanit("pin")}
          </div>
          <div className="min-w-0">
            <label className={etiketSinifi}>{cevir("Length")}</label>
            <SayiGirdisi className="h-kontrol w-full px-2.5" etiket={cevir("Length")} deger={referans.boy} onKaydet={(n) => setReferans((r) => ({ ...r, boy: n }))} />
            {kanit("boy")}
          </div>
        </div>
        <div className="mt-2 text-xs text-ink-3">{cevir("A field may stay empty; a cut at that flange weld then shows “at box flange weld” or “at pin flange weld” without a distance.")}</div>
      </div>

      <div className="mb-3 rounded-md border border-line p-3">
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2"><b className="text-sm">{cevir("Schematic")}</b><span className="text-xs text-ink-3">{cevir("not to scale · mm from box end")}</span></div>
        <KesimSemasi referans={referans} bantlar={semaBant} kesimler={semaKesim} parcalar={semaParca} digerler={semaDiger} bekleyenler={semaBekleyen}
          bulgudanKusur={bulgudanKusur} altSatirlar={semaAlt} />
      </div>

      {hesap.map((h) => {
        const a = h.is.anahtar;
        const m = h.oneri.kesimler.length;
        const bm = (j: Baglanti) => kaynakMalzemesi(j, { rotaKod: h.is.yanit.is.rotaKod, hat: h.is.yanit.is.hat }).join(" / ");
        const satirlar = [...h.t.kesimler].sort((x, y) => x.sira - y.sira);
        // D-054 (8): boydan boya önerisinde tek parça box flanş kaynağından pin flanş kaynağına → özet flanş adlarıyla.
        const baglantiOf = (sira: number) => h.t.kesimler.find((s) => s.sira === sira)?.baglanti;
        const tekParca = h.deg.parcalar.length === 1 ? h.deg.parcalar[0]! : null;
        const flanstanFlansa = h.oneri.tamBoy && tekParca !== null && baglantiOf(tekParca.basSira) === BOX_BAGLANTI && baglantiOf(tekParca.sonSira) === PIN_BAGLANTI;
        return (
          <div key={a} className="mb-3">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <b className="text-sm">{cevir`Cut points · ${isBasligi(h.is.yanit)}`}</b>
              <span className="text-xs text-ink-3">{h.oneri.tamBoy ? cevir("proposed: full-length replacement, one cut at each flange weld and one splice weld on the new pipe") : cevir("proposed: 300 mm outside each end of a range, 500 mm each side of a point")}</span>
            </div>
            <div className="scroll-thin overflow-x-auto rounded-md border border-line">
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr className="border-b border-line">
                    <th className={tabloBas}>{cevir("Cut")}</th>
                    <th className={tabloBas}>{cevir("Distance from box end (mm)")}</th>
                    <th className={tabloBas}>{cevir("Joint (new weld)")}</th>
                    <th className={tabloBas}>{cevir("Suggestion")}</th>
                    <th className={tabloBas}>{cevir("Resulting weld")}</th>
                    <th className={tabloBas} />
                  </tr>
                </thead>
                <tbody>
                  {satirlar.length === 0 && (
                    <tr><td colSpan={6} className={cn(tabloHucre, "text-ink-3")}>{cevir("No cut point proposed.")}</td></tr>
                  )}
                  {satirlar.map((s) => {
                    const ham = h.d.kesimler.find((x) => x.sira === s.sira) ?? s;
                    const etkin = s.durum === "onayli";
                    const eklenen = s.sira > m;
                    const flans = flansYazisi(s.baglanti);
                    const mmYazi = s.mesafeMm !== null ? mmMetni(s.mesafeMm) : flans ?? "—";
                    if (!etkin) {
                      const etiket = s.iptalNedeni === "birlestirme" ? cevir("cancelled (merged)") : s.iptalNedeni === "kesim_yok" ? cevir("cancelled (No cut)") : cevir("cancelled");
                      return (
                        <tr key={s.sira} className="border-b border-line text-ink-3 last:border-0">
                          <td className={tabloHucre}><b><s>{satirAdi(s)}</s></b></td>
                          <td className={tabloHucre}><s>{mmYazi}</s></td>
                          <td className={tabloHucre}>–</td>
                          <td className={tabloHucre}><Etiket>{etiket}</Etiket></td>
                          <td className={tabloHucre}>–</td>
                          <td className={cn(tabloHucre, "text-right")}>
                            {!(h.t.kesimYok && s.iptalNedeni === "kesim_yok") && <Buton kucuk onClick={() => geriAl(a, s.sira)}>{cevir("Restore")}</Buton>}
                          </td>
                        </tr>
                      );
                    }
                    const ipuclari = h.deg.ipuclari.filter((i) => i.sira === s.sira);
                    const tasindi = s.oneriBaglanti === BORU_BAGLANTI && flansMi(s.baglanti) && s.oneriMm !== null;
                    const flansKonumu = s.baglanti === BOX_BAGLANTI ? referans.box ?? 0 : referans.pin ?? boy;
                    const no = kaynakNoOf(h.t, s.sira);
                    const tamir = s.tur === "kaynak_tamiri";
                    // D-055: yeni borunun ek kaynağı: kesim değil; bağlantı sabit, mesafe isteğe bağlı (boşken hata değil).
                    const ek = ekKaynagiMi(s);
                    return (
                      <tr key={s.sira} className="border-b border-line last:border-0">
                        <td className={tabloHucre}>
                          <b>{satirAdi(s)}</b>
                          {ek && <div className="text-xs text-ink-3">{cevir("no cut")}</div>}
                        </td>
                        <td className={tabloHucre}>
                          <MesafeHucresi s={s} baglantiDegisti={h.d.baglantiDegisen.includes(s.sira)} onMesafe={(n) => mesafeYaz(a, ham, n)} onBeyan={(isaretli) => beyanDegistir(a, s.sira, isaretli)} />
                        </td>
                        <td className={tabloHucre}>
                          <select className={cn(kucukGirdi, "w-44")} value={s.baglanti} disabled={ek} aria-label={cevir("Joint (new weld)")} onChange={(e) => baglantiSec(a, s.sira, e.target.value as Baglanti)}>
                            {BAGLANTILAR.filter((b) => (ek ? b === BORU_BAGLANTI : !tamir || flansMi(b))).map((b) => <option key={b} value={b}>{b}</option>)}
                          </select>
                        </td>
                        <td className={tabloHucre}>
                          {tamir && <div className="text-xs text-ink-2">{cevir("Weld repair at existing weld")}</div>}
                          {ek && <div className="text-xs text-ink-2">{cevir("New pipe splice weld, no cut")}</div>}
                          {!tamir && !ek && tasindi && (
                            <>
                              <div className="text-xs text-ok">{s.baglanti === BOX_BAGLANTI ? cevir("Cut at box flange weld applied") : cevir("Cut at pin flange weld applied")}</div>
                              <div className="text-xs text-ink-3">{cevir`no ${mmMetni(Math.abs((s.oneriMm ?? 0) - flansKonumu))} mm stub left`}</div>
                            </>
                          )}
                          {!tamir && !ek && ipuclari.map((i, n) => (
                            <div key={n}>
                              <div className={cn("text-xs", i.tur === "birlestir" && !i.ilk ? "text-ink-3" : "text-warn")}>{cevir(ipucuMetni(i, h.etiket))}</div>
                              {i.tur === "flans_yakin" && (
                                <Buton kucuk className="mt-1" onClick={() => baglantiSec(a, s.sira, i.taraf === "box" ? BOX_BAGLANTI : PIN_BAGLANTI)}>
                                  {i.taraf === "box" ? cevir("Cut at box flange weld") : cevir("Cut at pin flange weld")}
                                </Buton>
                              )}
                              {i.tur === "birlestir" && i.ilk && (
                                <Buton kucuk className="mt-1" onClick={() => iptalEt(a, [s.sira, i.ile], "birlestirme")}>{cevir`Merge C${s.sira}–C${i.ile}`}</Buton>
                              )}
                            </div>
                          ))}
                          {!tamir && !ek && !tasindi && ipuclari.length === 0 && <span className="text-xs text-ink-3">—</span>}
                          <input type="text" className={cn(kucukGirdi, "mt-1 block w-full")} value={ham.not ?? ""}
                            placeholder={cevir("Note (optional)")}
                            onChange={(e) => { const v = e.target.value; satir(a, s.sira, (x) => ({ ...x, not: v })); }} />
                        </td>
                        <td className={tabloHucre}>
                          <KaynakHucresi s={s} no={no} malzeme={bm(s.baglanti)} onNo={(n) => satir(a, s.sira, (x) => ({ ...x, kaynakNo: n }))}
                            hatali={h.deg.engeller.some((e) => (e.tur === "kaynak_no" && e.sira === s.sira) || (e.tur === "kaynak_no_tekrar" && e.no === no) || (e.tur === "kaynak_no_bos" && e.siralar.includes(s.sira)))} />
                        </td>
                        <td className={cn(tabloHucre, "text-right")}>
                          {eklenen
                            ? <Buton kucuk className="whitespace-nowrap" onClick={() => kaldir(a, s.sira)}>{cevir("✕ Remove")}</Buton>
                            : <Buton kucuk className="whitespace-nowrap" onClick={() => iptalEt(a, [s.sira], "iptal")}>{ek ? cevir("✕ Cancel splice weld") : cevir("✕ Cancel cut")}</Buton>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* D-070: bir satır işaretliyken kaynak numarası önerilmez; "W" kutuları yalnız elle yazılanı gösterir. */}
            {h.numaraYazilir && <div className="mt-2 text-xs text-ink-2">{cevir("Weld numbers are not proposed while a row has no distance record: type every weld number as written on the weld map.")}</div>}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Buton kucuk disabled={h.t.kesimYok} onClick={() => ekle(a)}>{cevir("+ Add cut point")}</Buton>
              <Buton kucuk disabled={h.t.kesimYok} onClick={() => ekle(a, "ek_kaynagi")}>{cevir("+ Add splice weld")}</Buton>
              <Buton kucuk className={cn(h.t.kesimYok && "border-orange text-orange")} onClick={() => kesimYokDegistir(a)}>{h.t.kesimYok ? cevir("Undo No cut") : cevir("No cut")}</Buton>
              <span className="text-xs text-ink-3">{cevir("No cut sends the item with 0 welds (covered by another item, or weld repair); a reason is optional.")}</span>
            </div>

            {/* uyarılar: onayı durdurmaz */}
            {h.deg.uyarilar.length > 0 ? (
              <Band tip="not" className="mt-3">
                <b>{h.deg.uyarilar.length === 1 ? cevir("1 warning.") : cevir`${h.deg.uyarilar.length} warnings.`}</b> {cevir("Approve stays enabled.")}
                <ul className="mt-1 list-disc space-y-0.5 pl-5">{h.deg.uyarilar.map((u, i) => <li key={i}>{cevir(uyariMetni(u, h.etiket))}</li>)}</ul>
              </Band>
            ) : <Band tip="ok" className="mt-3">{cevir("No warnings.")}</Band>}

            {/* özet: yeni boru boyu yalnız bilgi, siparişe gitmez (Soru 10) */}
            <div className="mt-3 rounded-md border border-line bg-surface-2 px-3 py-2 text-sm">
              <b>{kesimAdedi(h.deg.etkinKesim)} · {kaynakAdedi(h.deg.kaynakSayisi)} · {formAdedi(h.deg.kaynakSayisi)}{h.deg.yeniBoruMm > 0 ? ` · ${cevir`new pipe ≈ ${mmMetni(h.deg.yeniBoruMm)} mm`}` : ""}</b>
              <div className="mt-0.5 text-xs text-ink-2">
                {[
                  flanstanFlansa ? cevir("Piece to replace: box flange weld – pin flange weld") : h.deg.parcalar.length ? cevir`Pieces to replace: ${parcaMetni(h.deg.parcalar)} mm` : null,
                  // D-055: yeni borunun ek kaynakları (kesim sayısına girmez; kaynak ve form sayısında var).
                  h.deg.ekKaynagi > 0 ? ekAdedi(h.deg.ekKaynagi) : null,
                  oneriOzetMetni(h.ozet, true),
                  // D-070: beyanlı bir kesim varken parçalar ve yeni boru boyu hesaplanmaz; son parça bunu söyler.
                  h.konumEksik
                    ? cevir("pieces to replace and new pipe length are not calculated: a cut has no distance record")
                    : cevir("new pipe length is information only, not sent to the order"),
                ].filter(Boolean).join(" · ")}
              </div>
            </div>

            <div className="mt-3">
              <label className={etiketSinifi}>{cevir("Reason (optional)")}</label>
              <textarea className={alan} rows={2} placeholder={cevir("Why the plan differs from the proposal")} value={h.d.gerekce}
                onChange={(e) => { const v = e.target.value; guncelle(a, (d) => ({ ...d, gerekce: v })); }} />
            </div>
          </div>
        );
      })}

      <Band tip="bilgi" className="mb-3">
        <b>{cevir("How this step works.")}</b>
        <ul className="mt-1 list-disc space-y-0.5 pl-5">
          {tamBoyVar && <li>{cevir("Full-length replacement: when the closing disposition or, without one, the recommended action says the whole length of pipe is replaced, two cuts are proposed, one at each flange weld. Defect locations stay on the schematic and are not cut points. A closing disposition that gives distances is followed as before.")}</li>}
          {/* D-055: "+ Add splice weld" her planda olduğu için madde hep görünür. */}
          <li>{cevir("New pipe splice weld: a row marked S is a weld on the new pipe where two pipe lengths are joined. It is not a cut; it creates one weld (Pipe to pipe butt) and one fit-up form. One is proposed for a full-length replacement; + Add splice weld adds one to any plan. Its distance is optional. Rows are numbered 1…n; C is a cut, S is a splice weld.")}</li>
          <li>{cevir("Choosing Box to pipe butt or Pin to pipe butt moves the distance to that flange weld and locks it (“at box flange weld”). Typing a distance away from the flange weld switches the joint back to Pipe to pipe butt.")}</li>
          <li>{cevir("Cut at box flange weld (or pin) does this in one click. It is suggested when a cut is less than 500 mm from the flange weld.")}</li>
          <li>{cevir("Merge cancels the two inner cuts, so two pieces become one. It is suggested when less than 4,000 mm of sound pipe is left between two pieces.")}</li>
          <li>{cevir("A cancelled cut stays in the list, struck through; Restore brings it back. A changed distance shows the proposed value struck through next to it.")}</li>
          {/* D-070: beyan yapılmış kesimler içindir (weld map'inden girilen bitmiş riser); yapılacak kesimde mesafe girilir. */}
          <li>{cevir("No distance record: when the weld map gives a Pipe to pipe butt weld without its position, tick “No distance record available” on that row and do not type a distance. It is meant for cuts that already exist (a finished riser entered from its weld map); for a cut that is still to be made, enter the distance. The row is approved with its joint and weld number only; the distance can be entered later on the job page. If you cannot tell which proposed cut a weld belongs to, cancel the proposed cuts and add cut points.")}</li>
          <li>{cevir("Weld numbers are proposed 1…n from the box end and can be changed; while a row has no distance record they are typed from the weld map. The base material follows the joint.")}</li>
        </ul>
        <b className="mt-2 block">{cevir("Approve stays disabled when")}</b>
        <ul className="mt-1 list-disc space-y-0.5 pl-5">
          {[
            cevir("an item on a Cutting route has no active cut and no No cut"),
            cevir`a distance is outside 0 to the tube length (${mmMetni(boy)} mm)`,
            cevir("two active cuts of this item are at the same distance"),
            cevir("a Pipe to pipe butt cut has neither a distance nor “No distance record available”"),
            cevir("a row has no distance record and a weld number is not typed"),
          ].map((m, i, l) => <li key={i}>{m}{i === l.length - 1 ? "." : ";"}</li>)}
        </ul>
        <b className="mt-2 block">{cevir("Warnings only")}</b>
        <ul className="mt-1 list-disc space-y-0.5 pl-5">
          {[
            cevir("a cut inside a defect zone"),
            cevir("a pipe to pipe cut without a pair"),
            cevir("a splice weld whose distance is outside the pieces to be replaced, or a splice weld in a plan without a piece to be replaced"),
            cevir("a remnant shorter than 500 mm"),
            cevir("a hydraulic line item on an MB or CKB route"),
            cevir("a row without a distance record (its position checks are skipped)"),
          ].map((m, i, l) => <li key={i}>{m}{i === l.length - 1 ? "." : ";"}</li>)}
        </ul>
      </Band>

      {onayDurumu}
      <div className="mt-1 text-xs text-ink-3">
        {triyaj
          ? (iptalVar
            ? (onerilenTalimat
              ? cevir`Approve creates the repair item with this plan (cancelled cuts kept), its ${kaynakAdedi(toplamKaynak)} and ${formAdedi(toplamKaynak)} (one per weld) in one step, writes the recommended action as the item's instruction and records your name and the time. The plan cannot be changed after approval. Back returns to step 1. Cancel sends nothing and keeps no draft.`
              : cevir`Approve creates the repair item with this plan (cancelled cuts kept), its ${kaynakAdedi(toplamKaynak)} and ${formAdedi(toplamKaynak)} (one per weld) in one step, writes the closing disposition as the item's instruction and records your name and the time. The plan cannot be changed after approval. Back returns to step 1. Cancel sends nothing and keeps no draft.`)
            : (onerilenTalimat
              ? cevir`Approve creates the repair item with this plan, its ${kaynakAdedi(toplamKaynak)} and ${formAdedi(toplamKaynak)} (one per weld) in one step, writes the recommended action as the item's instruction and records your name and the time. The plan cannot be changed after approval. Back returns to step 1. Cancel sends nothing and keeps no draft.`
              : cevir`Approve creates the repair item with this plan, its ${kaynakAdedi(toplamKaynak)} and ${formAdedi(toplamKaynak)} (one per weld) in one step, writes the closing disposition as the item's instruction and records your name and the time. The plan cannot be changed after approval. Back returns to step 1. Cancel sends nothing and keeps no draft.`))
          : (iptalVar
            ? cevir`Approve records this plan (cancelled cuts kept), its ${kaynakAdedi(toplamKaynak)} and ${formAdedi(toplamKaynak)} (one per weld) on this job and your name and the time. The plan cannot be changed after approval.`
            : cevir`Approve records this plan, its ${kaynakAdedi(toplamKaynak)} and ${formAdedi(toplamKaynak)} (one per weld) on this job and your name and the time. The plan cannot be changed after approval.`)}
      </div>
      {/* D-070: gri notun altı biçimi yeniden yazılmadı; pencerede beyanlı satır varken altına bu satır eklenir. */}
      {toplamMesafesiz > 0 && (
        <div className="mt-1 text-xs text-ink-3">{cevir("A distance approved as “No distance record available” can be entered later on the job page (Welds of this item). Nothing else of the plan changes after approval; weld numbers stay as approved.")}</div>
      )}
      <div className="mt-1 text-xs text-ink-3">
        {triyaj
          ? cevir("Who can approve: the Quality Lead only (PM and System Administrator always). For other users, ticking an item on a Cutting route leaves Next: cut plan disabled (“Cut plan approval: Quality Lead”) and the item stays waiting; items without cuts are sent as today.")
          : cevir("Who can approve: the Quality Lead only (PM and System Administrator always).")}
      </div>
      <div className="mt-2"><Hata mesaj={hata ?? null} /></div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Buton tur="birincil" islem="kesimPlani" disabled={engelVar} mesgul={mesgul} onClick={onayla}>{onayMetni}</Buton>
        {onGeri && <Buton disabled={mesgul} onClick={onGeri}>{cevir("Back")}</Buton>}
        <Buton disabled={mesgul} onClick={onIptal}>{cevir("Cancel")}</Buton>
      </div>
      {goruntuleyici}
    </div>
  );
}

/* ---------------- onaylı plan: salt okunur görünüm (Cutting adım detayı ve D-044 "Show plan") ---------------- */

/**
 * D-070: kesim satırındaki damga (sunucu JSON'u): "No distance record available" beyanı (`kesim.mesafe_yok`, onayda yazılır) ve
 * mesafenin sonradan girilmesi (`kesim.mesafe_girildi`). `tarih` ISO. Alanlar isteğe bağlı okunur (JSON kolonu).
 */
export interface MesafeDamgasi { kim?: string | null; rol?: string | null; tarih?: string | null }

/**
 * D-035/D-036: onaylı kesim planı, komponent GET'indeki biçimiyle (kesim_plani + kesim satırları, iptaller dahil).
 * D-070: satır sunucudan geldiği gibi beyan damgasını (`mesafeYok`; mesafe sonradan girilse de tarih olarak kalır) ve sonradan giriş
 * damgasını (`mesafeGirildi`) taşır; ikisi de eski satırlarda boş.
 */
export interface OnayliPlan {
  id: string; kaynak: { cumleler?: KaynakCumle[]; bantlar?: Konum[] } | null; referans: BoruReferansi; kesimYok: boolean; gerekce: string | null;
  onaylayan: string; onaylayanRol: Rol | null; onayTs: string;
  kesimler: (KesimSatiri & { id: string; mesafeYok?: MesafeDamgasi | null; mesafeGirildi?: MesafeDamgasi | null })[];
}
/** Plandan doğan kaynak (komponent GET'inin weld satırı): doğduğu kesim, güncel W numarası ("Change weld no." dahil) ve ana malzeme. */
export interface PlanKaynagi { no: number; kesimId: string | null; bm: string[] | null }

/**
 * Onaylı planın normalleştirilmiş taslağı; referans JSON'unda eksik alan boşa düşer (sunucudaki planTaslagi gibi).
 * D-070: saklanan beyan damgası alan paketinin anahtarına çevrilir (damga var ve mesafe hâlâ boşsa `mesafeKayitYok`); normalleştirme
 * anahtarı yalnız beyanlı satırda bırakır. Bu olmadan şema satırı, özet ve monitör saklanan her satırı beyansız sayardı.
 */
export function onayliTaslak(p: OnayliPlan): KesimPlaniTaslagi {
  const r: Partial<BoruReferansi> = p.referans ?? {};
  return kesimleriNormallestir({
    referans: { box: r.box ?? null, ara: Array.isArray(r.ara) ? r.ara : [], pin: r.pin ?? null, boy: r.boy ?? null },
    kesimYok: p.kesimYok, gerekce: p.gerekce,
    kesimler: p.kesimler.map((k) => ({ ...k, mesafeKayitYok: Boolean(k.mesafeYok) && k.mesafeMm === null })),
  });
}

/** D-070: mesafesi sonradan girilmiş satırın alt yazısı: "entered later · kişi · tarih saat". */
export const sonradanGirildiMetni = (d: MesafeDamgasi): string => cevir`entered later · ${d.kim ?? "—"} · ${tarihSaat(d.tarih)}`;
/**
 * D-070: planın bir satırı "No distance record available" beyanı taşıyor mu (damga; mesafe sonradan girilse de kalır). Salt okunur
 * görünümlerde "The plan does not change after approval" cümlesinin altına istisna cümlesini açar.
 */
export const beyanTasiyorMu = (p: OnayliPlan): boolean => p.kesimler.some((k) => Boolean(k.mesafeYok));
/** D-070: istisna cümlesi ("Show plan" penceresi ve Cutting adım detayı aynı cümleyi yazar). */
export const beyanIstisnaMetni = (): string =>
  cevir("Exception: a distance approved as “no distance record” can be entered later under Welds of this item; weld numbers do not change.");

/**
 * Onaylı planın salt okunur kesim tablosu; Cutting adım detayı ve "Show plan" penceresi aynı satırları çizer. İptal satırı
 * üstü çizili (birleştirme / iptal / No cut); değişen mesafe önerinin üstü çizili değeriyle ("proposed ~~700~~"). Doğan
 * kaynağın numarası kesime bağlı kaynaktan (weld.kesim_id; "Change weld no." sonrası da güncel), yoksa kesim satırının
 * kaynak_no'sundan; ana malzeme kaynaktan, yoksa bağlantıdan. `notlu`: Note kolonu (kesim notu, boşsa "–").
 * D-055: ek kaynağı satırı "S3" ve altında "new pipe splice weld (no cut)"; mesafesi yoksa "position not set".
 * D-070: beyanlı satırda mesafe hücresi ve doğan kaynağın yeri "no distance record" ("—" beyansız / eski kayıtta, "position not set"
 * beyansız boş ek kaynağında kalır); mesafesi sonradan girilmiş satırda değerin altında "entered later · kişi · tarih saat".
 */
export function OnayliKesimTablosu({ plan, kaynaklar, rotaKod, hat, notlu, className }: {
  plan: OnayliPlan; kaynaklar: PlanKaynagi[]; rotaKod: string; hat: string; notlu?: boolean; className?: string;
}) {
  const taslak = onayliTaslak(plan);
  const satirlar = [...plan.kesimler].sort((a, b) => a.sira - b.sira);
  return (
    <div className={cn("scroll-thin overflow-x-auto", className)}>
      <table className={cn("w-full", notlu ? "min-w-[900px]" : "min-w-[640px]")}>
        <thead>
          <tr className="border-b border-line">
            <th className={tabloBas}>{cevir("Cut")}</th><th className={tabloBas}>{cevir("Distance from box end (mm)")}</th><th className={tabloBas}>{cevir("Joint (new weld)")}</th>
            <th className={tabloBas}>{cevir("State")}</th><th className={tabloBas}>{cevir("Resulting weld")}</th>{notlu && <th className={tabloBas}>{cevir("Note")}</th>}
          </tr>
        </thead>
        <tbody>
          {satirlar.length === 0 && <tr><td colSpan={notlu ? 6 : 5} className={cn(tabloHucre, "text-ink-3")}>{cevir("No cut point proposed.")}</td></tr>}
          {satirlar.map((k) => {
            const s = taslak.kesimler.find((x) => x.sira === k.sira) ?? k;
            const flans = flansYazisi(s.baglanti);
            const ek = ekKaynagiMi(s);
            const beyanli = mesafeBeyanliMi(s);
            const mm = s.mesafeMm !== null ? mmMetni(s.mesafeMm) : beyanli ? cevir("no distance record") : ek ? cevir("position not set") : flans ?? "—";
            if (s.durum !== "onayli") {
              const durum = s.iptalNedeni === "birlestirme" ? cevir("cancelled (merged)") : s.iptalNedeni === "kesim_yok" ? cevir("cancelled (No cut)") : cevir("cancelled");
              return (
                <tr key={k.id} className="border-b border-line text-ink-3 last:border-0">
                  <td className={tabloHucre}><s>{satirAdi(s)}</s></td><td className={tabloHucre}><s>{mm}</s></td>
                  <td className={tabloHucre}>–</td><td className={tabloHucre}><Etiket>{durum}</Etiket></td><td className={tabloHucre}>–</td>
                  {notlu && <td className={tabloHucre}>{s.not ?? "–"}</td>}
                </tr>
              );
            }
            const w = kaynaklar.find((x) => x.kesimId === k.id);
            const no = w?.no ?? s.kaynakNo ?? kaynakNoOf(taslak, s.sira);
            const bm = (w?.bm?.length ? w.bm : kaynakMalzemesi(s.baglanti, { rotaKod, hat })).join(" / ");
            const yer = s.mesafeMm !== null ? (flans ? `${mmMetni(s.mesafeMm)} (${flans})` : mmMetni(s.mesafeMm)) : beyanli ? cevir("no distance record") : ek ? cevir("position not set") : flans ?? "—";
            // D-070: mesafe onaydan sonra girildiyse (kesim.mesafe_girildi) kim ve ne zaman girdiği değerin altında.
            const girildi = s.mesafeMm !== null ? k.mesafeGirildi ?? null : null;
            return (
              <tr key={k.id} className="border-b border-line last:border-0">
                <td className={tabloHucre}><b>{satirAdi(s)}</b>{ek && <div className="text-xs text-ink-3">{cevir("new pipe splice weld (no cut)")}</div>}</td>
                <td className={tabloHucre}>
                  {s.oneriMm !== null && s.oneriMm !== s.mesafeMm && <span className="text-xs text-ink-3">{cevir("proposed")} <s>{mmMetni(s.oneriMm)}</s> </span>}{yer}
                  {girildi && <span className="block text-xs text-ink-3">{sonradanGirildiMetni(girildi)}</span>}
                </td>
                <td className={tabloHucre}>{s.baglanti}</td>
                <td className={tabloHucre}><Etiket tone="ok">{cevir("approved")}</Etiket></td>
                <td className={tabloHucre}><b>W{no ?? "—"}</b> · {s.baglanti} · {mm} · {bm}</td>
                {notlu && <td className={cn(tabloHucre, !s.not && "text-ink-3")}>{s.not ?? "–"}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * D-044: onaylı kesim planının salt okunur görünümü, komponent sayfasındaki "Show plan" penceresinin gövdesi. Şema
 * (KesimSemasi), türe göre kaynak alıntıları ("rw s.N" çipleri Rework Scope PDF'ini açar, U-037) ve kesim tablosu
 * (OnayliKesimTablosu) onay penceresi ve Cutting adım detayıyla ortaktır. Veri onaylı planın kayıtlarındandır: kaynak
 * cümleler ve kusur bantları onay anında plana yazılanlar, öneri yeniden hesaplanmaz. Düzenleme yok, hiçbir şey yazılmaz.
 */
export function OnayliKesimPlani({ plan, kaynaklar, is, rework }: {
  plan: OnayliPlan;
  kaynaklar: PlanKaynagi[];
  /**
   * `baslik` "#1 Body overall + coating"; `etiket` "ST-03 Pipe cut + Replacement"; `kalemler` çok bulgulu işte alıntı başlıkları:
   * işin bütün bulguları, plan onaylandıktan sonra geri gönderilenler dahil (alıntıları planda kalır).
   */
  is: { baslik: string; etiket: string | null; rotaKod: string; hat: string; kalemler: { no: number; kalem: string }[] };
  rework?: ReworkBelgesi | null;
}) {
  const { ac, goruntuleyici } = useReworkPdf(rework);
  const taslak = onayliTaslak(plan);
  const r = taslak.referans;
  const ozet = kesimOzeti(taslak);
  const etkinKesim = taslak.kesimler.filter((s) => s.durum === "onayli" && s.tur === "kesim").length;
  // D-055: "2 cuts · 1 splice weld" (ek kaynağı kesim sayılmaz).
  const kesimSayisiMetni = kesimAdedi(etkinKesim) + (ozet.ekKaynagi ? ` · ${ekAdedi(ozet.ekKaynagi)}` : "");
  const cumleler = plan.kaynak?.cumleler ?? [];
  const nolar = [...new Set(cumleler.map((k) => k.no))];
  const mm = (n: number | null) => (n === null ? "—" : mmMetni(n));
  const referansHucresi = (ad: string, deger: string) => (
    <div className="min-w-0">
      <div className="text-xs text-ink-3">{ad}</div>
      <div className="text-sm text-ink"><b>{deger}</b></div>
    </div>
  );
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <b className="text-sm">{is.baslik}</b>
        {is.etiket && <Etiket>{is.etiket}</Etiket>}
        <span className="text-xs text-ink-3">{cevir("approved plan · distances in mm from the box end")}</span>
      </div>

      <div className="mb-3 rounded-md border border-line p-3">
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2"><b className="text-sm">{cevir("Schematic")}</b><span className="text-xs text-ink-3">{cevir("not to scale · mm from box end")}</span></div>
        {/* D-070: beyanlı satır çizilmez; şemanın altındaki satır sayar ("Not on the schematic, no distance record: C1 · C2"). */}
        <KesimSemasi referans={r} bantlar={plan.kaynak?.bantlar ?? []} kesimler={semaKesimleri(taslak)} parcalar={ozet.parcalar} altSatirlar={semaDisiSatirlari(semaDisiEtiketleri(taslak))} />
      </div>

      <div className="mb-3">
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <b className="text-sm">{cevir`Cut points · ${is.baslik}`}</b>
          <span className="text-xs text-ink-3">{plan.kesimYok ? cevir("No cut") : kesimSayisiMetni} ({oneriOzetMetni(ozet, false)})</span>
        </div>
        <OnayliKesimTablosu plan={plan} kaynaklar={kaynaklar} rotaKod={is.rotaKod} hat={is.hat} notlu className="rounded-md border border-line" />
        {ozet.parcalar.length > 0 && (
          <p className="mt-2 text-sm text-ink-2">{cevir`Replaced pieces: ${veIle(ozet.parcalar.map((p) => parcaMetni([p])))} mm from box end · new pipe ≈ ${mmMetni(ozet.yeniBoruMm)} mm (information only).`}</p>
        )}
      </div>

      <div className="mb-3 rounded-md border border-line p-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2"><b className="text-sm">{cevir("Tube reference")}</b><span className="text-xs text-ink-3">{cevir("mm from box end")}</span></div>
        <div className="mt-2 grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          {referansHucresi(cevir("Box flange weld"), mm(r.box))}
          {referansHucresi(cevir("Intermediate girth weld"), r.ara.length ? r.ara.map(mmMetni).join(", ") : "—")}
          {referansHucresi(cevir("Pin flange weld"), mm(r.pin))}
          {referansHucresi(cevir("Length"), mm(r.boy))}
        </div>
      </div>

      {cumleler.length > 0 && (
        <div className="mb-3 rounded-md border border-line p-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <b className="text-sm">{cevir("Source")}</b>
            {ac && cumleler.some((k) => k.sayfa !== null) && <span className="text-xs text-ink-3">{cevir("rw s.N opens the Rework Scope PDF at that page")}</span>}
          </div>
          {nolar.map((no, i) => {
            const kalem = no !== null ? is.kalemler.find((k) => k.no === no) : undefined;
            return (
              <div key={String(no)} className={cn(i > 0 && "mt-3 border-t border-dashed border-line pt-2")}>
                {/* Kalemi bulunamayan bulgu (onaydan sonra başka işe geçmiş) yine numarasıyla başlık alır. */}
                {nolar.length > 1 && no !== null && <div className="mt-2 text-sm font-medium">#{no}{kalem ? ` ${kalem.kalem}` : ""}</div>}
                <TureGoreAlintilar liste={cumleler.filter((k) => k.no === no)} ac={ac} />
              </div>
            );
          })}
        </div>
      )}

      <div className="grid gap-x-6 gap-y-1 text-sm">
        {plan.gerekce && <div><span className="text-ink-3">{cevir("Reason")}</span> {plan.gerekce}</div>}
        <div><span className="text-ink-3">{cevir("Approved by")}</span> {plan.onaylayan}{plan.onaylayanRol ? ` (${cevir(ROL_ETIKET[plan.onaylayanRol])})` : ""} · {tarihSaat(plan.onayTs)}</div>
      </div>
      <p className="mt-2 text-xs text-ink-3">{cevir("The plan does not change after approval. A difference found at cutting is written as a note on the Cutting step.")}</p>
      {/* D-070: planın bir satırı beyan taşıyorsa istisna cümlesi (giriş düğmesi bu pencerede değil, "Welds of this item"da). */}
      {beyanTasiyorMu(plan) && <p className="mt-1 text-xs text-ink-3">{beyanIstisnaMetni()}</p>}
      {goruntuleyici}
    </div>
  );
}
